import reportWorkLaunchError from '@/lib/reportWorkLaunchError';
/**
 * Desktop video-generation studio: cinema player, shot timeline, stills rail.
 * Assembles clips / keyframes / character refs from Work artifacts and
 * conversation files the same way a deck viewer assembles slides.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { Image } from 'expo-image';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';
import { coworkerApi } from '@/lib/api';
import { useCoworkerStore, useToastStore, useUIStore } from '@/store';
import { formatFileSize } from '@/constants/utils/files';
import { downloadMedia } from '@/constants/utils/mediaDownload';
import {
  applyVideoProjectTake,
  buildVideoProject,
  mediaBaseName,
  buildVideoRedoDirection,
  buildVideoStillsDirectionFromPicks,
  buildVideoTimelineDirection,
  orderVideoClips,
  videoTimelineOrderFromEvents,
  buildVideoStillTakeChoiceDirection,
  buildVideoTakeChoiceDirection,
  displayStillLabel,
  studioDirectionQuestionAnswer,
  titleCaseWords,
  videoProjectStitchPendingFromEvents,
  videoProjectCanReviewStills,
  videoProjectFinalTake,
  videoProjectStillsApprovedFromEvents,
  videoTakePicksFromEvents,
} from '@/constants/utils/video_project';
import RedoSheet from './VideoProjectRedoSheet';
import StudioScrollPane from './StudioScrollPane';
import StudioTimelineRail from './StudioTimelineRail';
import VideoProjectReferenceAudio, { collectVideoReferenceAudio } from './VideoProjectReferenceAudio';
import VideoPreviewPlayer from './VideoPreviewPlayer';
import { isWorkResumable, workPendingInteractionFromEvents } from '@/constants/work';

const ACTIVE_TASK = new Set(['queued', 'running']);
const EMPTY_EVENTS = [];
// Serialize updates per task, including across panel remounts. A slower request
// must never overwrite a later drag or arrive after stitch approval.
// These are state saves only; they never send chat messages or run the agent.
const timelineUpdates = new Map();
function queueTimelineUpdate(taskId, clips) {
  const previous = timelineUpdates.get(taskId) || Promise.resolve();
  const next = previous.catch(() => {}).then(async () => {
    const timeline = await coworkerApi.saveVideoTimeline(taskId, clips);
    const excluded = clips.filter(clip => clip.excluded).map(clip => clip.id);
    if (excluded.length && (JSON.stringify(timeline.excludedClips?.map(clip => clip.id)) !== JSON.stringify(excluded)
      || JSON.stringify(timeline.clips?.map(clip => clip.id)) !== JSON.stringify(clips.filter(clip => !clip.excluded).map(clip => clip.id)))) {
      throw new Error('The execution service did not save the excluded scenes. Update the execution service before stitching this cut.');
    }
    return timeline;
  });
  timelineUpdates.set(taskId, next);
  const cleanup = () => { if (timelineUpdates.get(taskId) === next) timelineUpdates.delete(taskId); };
  next.then(cleanup, cleanup);
  return next;
}

async function sendStudioDirection(taskId, text, { freshDirection = false } = {}) {
  const trimmed = String(text || '').trim();
  if (!taskId || !trimmed) return false;
  const store = useCoworkerStore.getState();
  const status = store.tasks?.[taskId]?.status;
  const events = store.eventsByTask?.[taskId] || EMPTY_EVENTS;
  const pending = workPendingInteractionFromEvents(events)?.question;
  // A finished run can retain an unanswered review card in its event history.
  // Resume that run with the direction; answering its dead waiter does no work.
  const finished = status === 'completed' || status === 'cancelled' || status === 'canceled' || isWorkResumable(status);
  const ask = finished || freshDirection ? null : studioDirectionQuestionAnswer(pending, trimmed);
  if (ask) {
    await coworkerApi.answerQuestion(taskId, ask);
    return true;
  }
  if (ACTIVE_TASK.has(status)) {
    await store.steerTask(taskId, trimmed);
    return true;
  }
  if (isWorkResumable(status)) {
    await store.resumeTask(taskId, trimmed);
    store.notifyExternalFollowUp?.(taskId);
    return true;
  }
  await store.sendMessage(taskId, trimmed);
  store.notifyExternalFollowUp?.(taskId);
  return true;
}

const STAGE_BLACK = '#0B0B0D';
const READY = '#34D399';
const STILL = '#FBBF24';
const PLANNED = '#64748B';

function formatClock(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function statusColor(status) {
  if (status === 'ready') return READY;
  if (status === 'still') return STILL;
  return PLANNED;
}

function statusLabel(status) {
  if (status === 'ready') return 'Ready';
  if (status === 'still') return 'Image ready';
  return 'Planned';
}

function itemPath(item) {
  return item?.path || item?.source?.path || '';
}

function itemDirectUrl(item) {
  return item?.url
    || item?.source?.cdnPath
    || item?.source?.url
    || item?.source?.src
    || '';
}

function selectedTake(item) {
  const takeId = item?.mediaId || item?.selectedTakeId;
  if (!takeId || !item?.takes?.length) return null;
  return item.takes.find((entry) => entry.id === takeId) || null;
}

function itemDownloadName(item, { still = false } = {}) {
  const take = selectedTake(item);
  const raw = take?.name
    || take?.path
    || item?.name
    || item?.path
    || item?.source?.name
    || item?.source?.path
    || '';
  const base = String(raw).split(/[/\\]/).pop().split('?')[0];
  if (base && /\.[a-z0-9]{2,5}$/i.test(base)) return base;
  if (still || item?.kind) return `${base || 'still'}.png`;
  return `${base || 'video'}.mp4`;
}

function resolveMediaUrl(item, urls) {
  if (!item || (item.role === 'clip' && item.status !== 'ready')) return '';
  const take = selectedTake(item);
  if (take) return urls?.[take.id] || itemDirectUrl(take) || '';
  return urls?.[item.id] || itemDirectUrl(item);
}

function collectResolvableItems(project) {
  const items = [];
  const seen = new Set();
  const push = (item) => {
    if (!item?.id || seen.has(item.id)) return;
    seen.add(item.id);
    items.push(item);
  };
  for (const item of project.playable || []) {
    push(item);
    push(item.still);
    for (const take of item.takes || []) push(take);
    for (const take of item.still?.takes || []) push(take);
  }
  for (const item of project.stills || []) {
    push(item);
    for (const take of item.takes || []) push(take);
  }
  return items;
}

function useResolvedUrls(project, taskId) {
  const [resolved, setResolved] = useState({ taskId, urls: {} });
  const recoveryRef = useRef(null);
  const items = collectResolvableItems(project);
  const mediaKey = item => `${itemDirectUrl(item)}:${itemPath(item)}`;
  const currentMediaKeys = useRef(new Map());
  currentMediaKeys.current = new Map(items.map(item => [item.id, mediaKey(item)]));
  useEffect(() => {
    const recovery = { active: true, pending: new Map(), blobs: new Map(), nativeUrls: new Map(), nativeAttempts: new Map() };
    recoveryRef.current = recovery;
    return () => {
      recovery.active = false;
      for (const url of recovery.blobs.values()) URL.revokeObjectURL(url);
    };
  }, [taskId]);
  const recoverUrl = useCallback(async (item, { force = false } = {}) => {
    const media = selectedTake(item) || item;
    const path = itemPath(media);
    const recovery = recoveryRef.current;
    if (!taskId || !path || !recovery?.active || !['web', 'macos'].includes(Platform.OS)) return;
    const key = mediaKey(media);
    const pendingKey = JSON.stringify([media.id, key]);
    if (Platform.OS === 'macos' && !force && recovery.nativeAttempts.get(media.id) === key) return;
    if (recovery.blobs.has(media.id)) return;
    if (recovery.pending.has(pendingKey)) return recovery.pending.get(pendingKey);
    const pending = (async () => {
      try {
        if (Platform.OS === 'macos') {
          // Native AVPlayer needs a fresh signed URL, not a browser Blob URL.
          // Try automatically once per source; explicit Retry may try again.
          recovery.nativeAttempts.set(media.id, key);
          const url = await coworkerApi.getArtifactDownloadUrl(taskId, path);
          if (!url || !recovery.active || currentMediaKeys.current.get(media.id) !== key) return;
          recovery.nativeUrls.set(media.id, { key, url });
          setResolved(prev => ({ taskId, urls: { ...(prev.taskId === taskId ? prev.urls : {}), [media.id]: url } }));
          return;
        }
        const response = await coworkerApi.fetchArtifactBinary(taskId, path, { asProxy: true });
        if (!response.ok) return;
        const blob = await response.blob();
        if (!recovery.active || currentMediaKeys.current.get(media.id) !== key) return;
        const url = URL.createObjectURL(blob);
        recovery.blobs.set(media.id, url);
        setResolved(prev => ({ taskId, urls: { ...(prev.taskId === taskId ? prev.urls : {}), [media.id]: url } }));
      } catch { /* Keep the retry action available when the workspace is unavailable. */ }
      finally { recovery.pending.delete(pendingKey); }
    })();
    recovery.pending.set(pendingKey, pending);
    return pending;
  }, [taskId]);
  const urls = resolved.taskId === taskId ? resolved.urls : {};
  // The path can appear before its CDN upload finishes. artifact_ready adds
  // a file ID later; retry a previously unavailable URL at that transition.
  const signature = items.map((item) => `${item.id}:${itemDirectUrl(item)}:${itemPath(item)}:${item.source?.file?.id || item.source?.file?._id || ''}`).join('|');

  useEffect(() => {
    let cancelled = false;
    const next = {};
    const pending = [];
    for (const item of items) {
      const direct = itemDirectUrl(item);
      if (direct) {
        const nativeRecovery = recoveryRef.current?.nativeUrls.get(item.id);
        next[item.id] = recoveryRef.current?.blobs.get(item.id)
          || (nativeRecovery?.key === mediaKey(item) ? nativeRecovery.url : direct);
        continue;
      }
      const path = itemPath(item);
      if (taskId && path) pending.push({ id: item.id, path });
    }
    setResolved((prev) => ({ taskId, urls: {
      ...Object.fromEntries(Object.entries(prev.taskId === taskId ? prev.urls : {}).filter(([id]) => items.some((item) => item.id === id))),
      ...next,
    } }));
    if (!pending.length) return undefined;
    (async () => {
      const resolved = {};
      await Promise.all(pending.map(async ({ id, path }) => {
        try {
          const url = await coworkerApi.getArtifactDownloadUrl(taskId, path);
          if (url) resolved[id] = url;
        } catch { /* clip may still be uploading */ }
      }));
      if (!cancelled && Object.keys(resolved).length) {
        setResolved((prev) => ({ taskId, urls: { ...(prev.taskId === taskId ? prev.urls : {}), ...resolved } }));
      }
    })();
    return () => { cancelled = true; };
    // signature stands in for the item list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, taskId]);

  return { urls, recoverUrl };
}

function CinemaVideo({ url, name, onDuration, onError, onRetry }) {
  const videoRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  useEffect(() => setFailed(false), [url]);

  const handleMeta = useCallback((event) => {
    const duration = event?.nativeEvent?.duration
      ?? event?.target?.duration
      ?? videoRef.current?.duration;
    if (Number.isFinite(duration) && duration > 0) onDuration?.(duration);
  }, [onDuration]);

  if (!url) return null;
  if (failed) return <View style={styles.stageEmpty}><Text style={{ color: '#F8FAFC' }}>This preview could not load</Text><Pressable accessibilityRole="button" accessibilityLabel="Retry video preview" disabled={retrying}
    onPress={async () => { setRetrying(true); try { await onRetry?.(); } finally { setRetrying(false); setFailed(false); } }}
    style={styles.sheetGhost}><Text style={{ color: '#F8FAFC' }}>{retrying ? 'Loading…' : 'Try again'}</Text></Pressable></View>;

  if (Platform.OS !== 'web') return <VideoPreviewPlayer url={url} title={name}
    onMetadata={({ duration }) => onDuration?.(duration)}
    onError={() => { setFailed(true); onError?.(); }} />;

  return (
    // eslint-disable-next-line react/forbid-elements
    <video
      ref={videoRef}
      key={url}
      controls
      playsInline
      autoPlay={false}
      preload="metadata"
      src={url}
      onError={() => { setFailed(true); onError?.(); }}
      onLoadedMetadata={handleMeta}
      aria-label={name || 'Video'}
      style={{
        width: '100%',
        height: '100%',
        display: 'block',
        objectFit: 'contain',
        backgroundColor: STAGE_BLACK,
      }}
    />
  );
}

function StageEmpty({ colors, title, subtitle, icon }) {
  return (
    <View style={styles.stageEmpty}>
      <View style={[styles.stageEmptyIcon, { backgroundColor: 'rgba(255,255,255,0.06)' }]}>
        <Icon name={icon || 'clapperboard'} size={18} color={colors.text_color_tertiary} />
      </View>
      <Text size="s" weight="medium" style={{ color: '#E5E7EB' }}>{title}</Text>
      {subtitle ? (
        <Text size="xxs" style={{ color: '#94A3B8', textAlign: 'center', maxWidth: 280 }}>
          {subtitle}
        </Text>
      ) : null}
    </View>
  );
}

function ClipPosterVideo({ url }) {
  const videoRef = useRef(null);
  const framedRef = useRef(false);

  useEffect(() => {
    framedRef.current = false;
  }, [url]);

  const paintFirstFrame = useCallback(() => {
    const el = videoRef.current;
    if (!el || framedRef.current) return;
    const duration = Number(el.duration);
    if (!Number.isFinite(duration) || duration <= 0) return;
    framedRef.current = true;
    try {
      el.currentTime = Math.min(0.35, duration * 0.08);
    } catch { /* seek may throw before the buffer is ready */ }
  }, []);

  if (!url) return null;
  if (Platform.OS === 'macos') {
    const VideoStudioPoster = require('./VideoStudioPoster.macos').default;
    return <VideoStudioPoster url={url} />;
  }
  if (Platform.OS !== 'web') return null;

  return (
    // eslint-disable-next-line react/forbid-elements
    <video
      ref={videoRef}
      muted
      playsInline
      preload="metadata"
      src={url}
      onLoadedMetadata={paintFirstFrame}
      onLoadedData={paintFirstFrame}
      aria-hidden
      style={{
        width: '100%',
        height: '100%',
        display: 'block',
        objectFit: 'cover',
        backgroundColor: STAGE_BLACK,
        pointerEvents: 'none',
      }}
    />
  );
}

function timelineCardName(item) {
  if (item?.role === 'film') return 'Full film';
  if (item?.role === 'segment') return item.label || item.name;
  if (Number.isFinite(item?.index)) {
    if (item.status === 'ready') return `Shot ${item.index}`;
    if (item.status === 'still') return `Shot ${item.index} · still`;
    return `Shot ${item.index} · planned`;
  }
  return item?.name || 'Shot';
}

function stopCardEvent(event) {
  event?.stopPropagation?.();
  event?.preventDefault?.();
}

function CardDownload({ label, disabled, onPress }) {
  if (!onPress) return null;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      disabled={disabled}
      onPress={(event) => {
        stopCardEvent(event);
        if (!disabled) onPress();
      }}
      style={({ hovered, pressed }) => [
        styles.cardDownload,
        (hovered || pressed) && !disabled && { backgroundColor: 'rgba(0,0,0,0.88)' },
        disabled && { opacity: 0.35 },
      ]}
    >
      <Icon name="arrow-down-to-line" size={10} color="#F8FAFC" />
    </Pressable>
  );
}

function StudioDownloadChip({ label, disabled, colors, onPress }) {
  if (!onPress) return null;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      disabled={disabled}
      onPress={onPress}
      style={({ hovered, pressed }) => [
        styles.actionChip,
        { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_secondary },
        (hovered || pressed) && !disabled && { backgroundColor: colors.solid_color_tertiary },
        disabled && { opacity: 0.55 },
      ]}
    >
      <Icon name="arrow-down-to-line" size={11} color={colors.text_color_secondary} />
      <Text size="xxs" weight="medium" color="secondary">
        Download
      </Text>
    </Pressable>
  );
}

function TimelineCard({ item, fluid, compact, dense, selected, finalTake, selectionPending, posterUrl, videoUrl, colors, onPress, onDownload }) {
  const isFilm = item.role === 'film';
  const isSegment = item.role === 'segment';
  const badge = isFilm
    ? 'Video'
    : isSegment
      ? (item.label || `Seg ${item.index ?? ''}`)
      : Number.isFinite(item.index) ? String(item.index).padStart(2, '0') : 'Clip';
  const tint = statusColor(item.status || 'ready');
  const label = timelineCardName(item);

  return (
    <View
      style={[
        styles.clipCard,
        dense && styles.denseClipCard,
        fluid && styles.fluidCard,
        { borderColor: selected ? colors.accent_color_primary : colors.border_color_secondary },
        selected && { backgroundColor: colors.solid_color_secondary },
        item.excluded && { opacity: 0.5 },
      ]}
    >
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={item.label || label}
        accessibilityState={{ selected }} aria-pressed={selected}
        onPress={onPress}
        style={({ hovered }) => [
          styles.cardHit,
          dense && styles.denseCardHit,
          hovered && !selected && { borderColor: colors.border_color_primary },
        ]}
      >
        <View style={[styles.clipPoster, fluid && styles.fluidPoster, compact && styles.compactClipPoster, dense && styles.denseClipPoster, { backgroundColor: STAGE_BLACK }]}>
          {posterUrl ? (
            <Image source={{ uri: posterUrl }} style={styles.clipPosterImage} contentFit="cover" />
          ) : videoUrl ? (
            <ClipPosterVideo url={videoUrl} />
          ) : (
            <View style={styles.clipPosterFallback}>
              <Icon
                name={isFilm ? 'film' : 'clapperboard'}
                size={14}
                color={item.status === 'ready' ? '#E5E7EB' : '#64748B'}
              />
            </View>
          )}
          <View style={[styles.clipBadge, { backgroundColor: 'rgba(0,0,0,0.72)' }]}>
            <Text size="xxs" weight="semibold" style={{ color: '#F8FAFC' }}>{badge}</Text>
          </View>
          <View style={[styles.clipDot, { backgroundColor: tint }]} />
          {(item.takes?.length || 0) > 1 ? (
            <View style={[styles.takeBadge, { backgroundColor: 'rgba(0,0,0,0.72)' }]}>
              <Text size="xxs" weight="semibold" style={{ color: '#F8FAFC' }}>
                {finalTake?.takeLabel || item.takes.find((take) => take.id === item.selectedTakeId)?.takeLabel || `Take ${item.takes.length}`}
              </Text>
            </View>
          ) : null}
        </View>
        <Text size="xs" weight={selected ? 'semibold' : 'medium'} numberOfLines={1} style={[styles.clipName, dense && styles.denseClipName]}>
          {dense && item.shot?.duration ? `${label} · ${item.shot.duration}` : label}
        </Text>
        {!dense ? <Text size="xxs" color="secondary" numberOfLines={1} style={styles.clipName}>{[statusLabel(item.status), item.shot?.duration].filter(Boolean).join(' · ')}</Text> : null}
        <View style={styles.timelineTakeSelection}>
          {item.excluded ? <Text size="xxs" weight="semibold" color="secondary">Excluded</Text> : finalTake ? <>
            <Icon name="check-circle" size={10} color={READY} />
            <Text size="xxs" weight="medium" numberOfLines={1} style={{ color: READY, flexShrink: 1 }}>{finalTake.takeLabel} · Final video</Text>
          </> : selectionPending && item.role === 'clip' && item.status === 'ready'
            ? <Text size="xxs" color="secondary" numberOfLines={1}>Checking final take…</Text>
            : dense ? <Text size="xxs" color="secondary" numberOfLines={1}>{statusLabel(item.status)}</Text> : null}
        </View>
      </Pressable>
      <CardDownload
        label={`Download ${item.label || label}`}
        disabled={!videoUrl && !posterUrl}
        onPress={onDownload}
      />
    </View>
  );
}

function StillCard({ item, fluid, selected, chosenTake, url, colors, onPress, onDownload }) {
  const label = displayStillLabel(item);
  return (
    <View
      style={[
        styles.stillCard,
        fluid && styles.fluidCard,
        { borderColor: selected ? colors.accent_color_primary : colors.border_color_secondary },
      ]}
    >
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ selected }} aria-pressed={selected}
        onPress={onPress}
        style={styles.cardHit}
      >
        <View style={[styles.stillPoster, fluid && styles.fluidPoster, { backgroundColor: colors.solid_color_secondary }]}>
          {url ? (
            <Image source={{ uri: url }} style={styles.clipPosterImage} contentFit={item.kind === 'master' ? 'cover' : 'contain'} />
          ) : (
            <Icon name="image" size={14} color={colors.text_color_tertiary} />
          )}
          {chosenTake || (item.takes?.length || 0) > 1 ? (
            <View style={[styles.takeBadge, styles.stillTakeBadge, { backgroundColor: 'rgba(0,0,0,0.72)' }]}>
              <Text size="xxs" weight="semibold" style={{ color: chosenTake ? READY : '#F8FAFC' }}>
                {chosenTake ? `✓ Selected · ${chosenTake.takeLabel}` : item.takes.find((take) => take.id === item.selectedTakeId)?.takeLabel
                  || `${item.takes.length} takes`}
              </Text>
            </View>
          ) : null}
        </View>
        <Text size="xxs" color="tertiary" numberOfLines={1}>{label}</Text>
      </Pressable>
      <CardDownload
        label={`Download ${label}`}
        disabled={!url}
        onPress={onDownload}
      />
    </View>
  );
}

function stillLockNoun(item) {
  if (item?.kind === 'master') return 'master';
  if (item?.kind === 'sheet') return 'sheet';
  if (Number.isFinite(item?.index)) return 'keyframe';
  return 'still';
}

function TakeLockButton({ item, colors, onLock, lockedTakeId, lockKind, lockBusy, disabled = false }) {
  const takes = item?.takes || [];
  if (!takes.length || (lockKind !== 'still' && takes.length < 2) || !lockKind || !onLock) return null;
  const selectedTake = takes.find((take) => take.id === item.selectedTakeId);
  const previewIsLocked = !!selectedTake && selectedTake.id === lockedTakeId;
  const stillNoun = stillLockNoun(item);
  const lockTag = lockKind === 'still' ? stillNoun : 'film';
  const lockLabel = !selectedTake
    ? ''
    : lockKind === 'still'
      ? `Use ${selectedTake.takeLabel} as the ${stillNoun}`
      : `Use ${selectedTake.takeLabel} in the film`;
  const lockedLabel = selectedTake
    ? `${selectedTake.takeLabel} locked as the ${lockTag}`
    : '';
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={previewIsLocked ? lockedLabel : lockLabel}
      disabled={disabled || previewIsLocked || lockBusy || !selectedTake}
      onPress={() => onLock(selectedTake?.id)}
      style={({ hovered, pressed }) => [
        styles.actionChip,
        { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_secondary },
        (hovered || pressed) && !previewIsLocked && { backgroundColor: colors.solid_color_tertiary },
        (disabled || lockBusy) && { opacity: 0.55 },
      ]}
    >
      <Icon name={previewIsLocked ? 'check' : (lockKind === 'still' ? 'image' : 'clapperboard')} size={11} color={colors.text_color_secondary} />
      <Text size="xxs" weight="medium" color="secondary">
        {lockBusy
          ? 'Saving choice…'
          : previewIsLocked
            ? (lockKind === 'still' ? 'Selected for this shot' : 'Selected')
            : lockKind === 'still' ? 'Use this image' : `Use ${selectedTake?.takeLabel || 'this version'}`}
      </Text>
    </Pressable>
  );
}

function TakePicker({ item, colors, onSelect, onLock, lockedTakeId, resolveUrl, lockKind, lockBusy, lockStatus, editingLocked }) {
  const takes = item?.takes || [];
  if (takes.length < 2) return null;
  const selectedTake = takes.find((take) => take.id === item.selectedTakeId);
  const lockedTake = takes.find((take) => take.id === lockedTakeId);
  const finalTake = lockKind === 'clip' && !lockStatus && !item.excluded ? videoProjectFinalTake(item, lockedTakeId) : null;
  const previewIsLocked = !!(lockedTake && selectedTake && lockedTake.id === selectedTake.id);
  const stillNoun = stillLockNoun(item);
  const lockTag = lockKind === 'still' ? stillNoun : 'film';
  const hint = lockStatus || (!lockKind || lockKind === 'clip'
    ? ''
    : previewIsLocked
      ? `${lockedTake.takeLabel} is selected for this shot.`
      : lockedTake
        ? `Previewing ${selectedTake?.takeLabel || 'this take'}. ${lockedTake.takeLabel} is still selected for this shot.`
        : `Previewing ${selectedTake?.takeLabel || 'this take'}. Choose a version, or keep the latest.`);
  return (
    <View style={styles.takeRow}>
      <Text size="xxs" weight="semibold" color="tertiary" style={styles.takeRowLabel}>
        Versions
      </Text>
      {hint ? (
        <Text size="xxs" color="secondary">{hint}</Text>
      ) : null}
      <StudioTimelineRail colors={colors} accessibilityLabel="Take versions" previousLabel="Previous takes" nextLabel="Next takes"
        resetKey={item.id} contentContainerStyle={styles.takeRailContent}>
      <View style={styles.takeChips} accessibilityRole="tablist" accessibilityLabel="Preview versions">
        {takes.map((take) => {
          const selected = take.id === item.selectedTakeId;
          const locked = take.id === lockedTakeId;
          const usedInFinal = take.id === finalTake?.id;
          const chosenImage = lockKind === 'still' && locked;
          const highlighted = usedInFinal || chosenImage;
          const thumb = resolveUrl?.(take) || take.url;
          return (
            <Pressable
              key={take.id}
              accessibilityRole="tab"
              accessibilityState={{ selected }}
              aria-selected={selected}
              accessibilityLabel={
                usedInFinal && !locked
                  ? `${take.takeLabel} for shot ${item.index}, used in final video by default${selected ? ', previewing' : ''}`
                  : locked
                  ? `${take.takeLabel} for ${Number.isFinite(item.index) ? `shot ${item.index}` : stillNoun}, locked as the ${lockTag}`
                  : `${take.takeLabel} for ${Number.isFinite(item.index) ? `shot ${item.index}` : (item.label || stillNoun)}, preview`
              }
              onPress={() => onSelect?.(take.id)}
              style={({ hovered }) => [
                styles.takeChip,
                {
                  borderColor: highlighted
                    ? READY
                    : locked
                    ? colors.accent_color_primary
                    : selected ? colors.border_color_primary : colors.border_color_secondary,
                  backgroundColor: highlighted ? accentWash(READY, 0.1) : selected ? colors.solid_color_secondary : colors.solid_color_primary,
                },
                hovered && !selected && !highlighted && { borderColor: colors.border_color_primary },
              ]}
            >
              <View style={styles.takeThumb}>
                {thumb ? (
                  item.role === 'clip' ? <ClipPosterVideo url={thumb} /> : <Image source={{ uri: thumb }} style={StyleSheet.absoluteFill} contentFit="cover" />
                ) : null}
                {highlighted ? <View accessible accessibilityLabel={chosenImage ? 'Selected image version' : 'Used in final video'} style={styles.takeFinalBadge}>
                  <Icon name="check-circle" size={13} color={READY} />
                </View> : null}
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text size="xxs" numberOfLines={1} weight={selected || locked || usedInFinal ? 'semibold' : 'medium'}>
                  {take.takeLabel}
                </Text>
                {chosenImage ? <Text size="xxs" weight="semibold" style={{ color: READY }}>Selected</Text> : null}
              </View>
            </Pressable>
          );
        })}
      </View>
      </StudioTimelineRail>
      <TakeLockButton item={item} colors={colors} onLock={onLock} lockedTakeId={lockedTakeId}
        lockKind={lockKind} lockBusy={lockBusy} disabled={editingLocked} />
    </View>
  );
}

function accentWash(hex, alpha = 0.2) {
  const value = String(hex || '').replace('#', '');
  if (value.length !== 6) return `rgba(251, 191, 36, ${alpha})`;
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function ReviewBanner({ title, body, actionLabel, actionIcon, accessibilityLabel, onAction, busy, colors }) {
  return (
    <View
      style={[
        styles.reviewCard,
        {
          borderColor: colors.accent_color_primary,
          backgroundColor: accentWash(colors.accent_color_primary, 0.18),
        },
      ]}
    >
      <View style={{ flex: 1, minWidth: 180, gap: 4 }}>
      <Text size="xs" weight="semibold">{title}</Text>
      <Text size="xxs" color="secondary">{body}</Text>
      {busy ? <Text size="xxs" color="secondary">Preparing your project…</Text> : null}
      </View>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        disabled={busy}
        onPress={onAction}
        style={({ hovered, pressed }) => [
          styles.reviewAction,
          { backgroundColor: colors.text_color_primary },
          (hovered || pressed) && { opacity: 0.9 },
          busy && { opacity: 0.55 },
        ]}
      >
        <Icon name={actionIcon} size={14} color={colors.solid_color_primary} />
        <Text size="xs" weight="semibold" style={{ color: colors.solid_color_primary }}>
          {actionLabel}
        </Text>
      </Pressable>
    </View>
  );
}

function stillRedoCopy(item) {
  if (!item) return null;
  const label = displayStillLabel(item);
  if (item.kind === 'master' || item.kind === 'sheet') {
    const who = titleCaseWords(item.character || 'character');
    return {
      text: `Redo the ${who} ${item.kind} still. Do not animate yet.`,
      toast: `Asked Work to redo the ${who} ${item.kind}`,
      label: `Redo ${label}`,
    };
  }
  if (Number.isFinite(item.index)) {
    return {
      text: `Redo shot ${item.index} keyframe. Do not animate yet.`,
      toast: `Asked Work to redo shot ${item.index} still`,
      label: `Redo ${label}`,
    };
  }
  return {
    text: `Redo the still ${item.name}. Do not animate yet.`,
    toast: 'Asked Work to redo that still',
    label: 'Redo this still',
  };
}

function StillInspector({ item, compact, choicesBusy, referencesLocked, colors, onRedo, onDownload, canDownload, onSelectTake, onLockTake, lockedTakeId, resolveUrl, redoBusy, lockStatus }) {
  if (!item) return null;
  const heading = item.kind === 'keyframe' && Number.isFinite(item.index)
    ? `Shot ${item.index} image`
    : displayStillLabel(item);
  const redo = stillRedoCopy(item);
  const selectedTake = item.takes?.find((take) => take.id === item.selectedTakeId);
  return (
    <View style={[styles.inspector, compact && { paddingHorizontal: 0 }]}>
      <View style={styles.inspectorHead}>
        <Text size="s" weight="semibold">{heading}</Text>
        <Text size="xxs" color="tertiary">
          {[
            item.kind === 'keyframe' ? 'Scene still' : item.kind === 'sheet' ? 'Character sheet' : 'Master reference',
            selectedTake?.takeLabel,
          ].filter(Boolean).join(' · ')}
        </Text>
      </View>
      <View style={styles.inspectorActions}>
      <StudioDownloadChip
        label={`Download ${displayStillLabel(item)}`}
        disabled={!canDownload}
        colors={colors}
        onPress={onDownload}
      />
      {onRedo && redo ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={redo.label}
          disabled={redoBusy || referencesLocked}
          onPress={() => onRedo(item)}
          style={({ hovered, pressed }) => [
            styles.actionChip,
            { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_secondary },
            (hovered || pressed) && { backgroundColor: colors.solid_color_tertiary },
            (redoBusy || referencesLocked) && { opacity: 0.55 },
          ]}
        >
          <Icon name="arrow-rotate-right" size={11} color={colors.text_color_secondary} />
          <Text size="xxs" weight="medium" color="secondary">
            {redoBusy ? 'Sending…' : 'Improve this image'}
          </Text>
        </Pressable>
      ) : null}
      <TakeLockButton item={item} colors={colors} onLock={onLockTake} lockedTakeId={lockedTakeId}
        lockKind="still" lockBusy={redoBusy || choicesBusy} disabled={referencesLocked} />
      </View>
      <TakePicker
        item={item}
        colors={colors}
        onSelect={onSelectTake}
        lockedTakeId={lockedTakeId}
        resolveUrl={resolveUrl}
        lockKind="still"
        lockBusy={redoBusy || choicesBusy}
        lockStatus={lockStatus}
      />
    </View>
  );
}

function Inspector({ item, compact, choicesBusy, colors, onRedo, onDownload, canDownload, onSelectTake, onLockTake, lockedTakeId, resolveUrl, redoBusy, mediaDuration, lockStatus, onToggleExcluded, editingLocked }) {
  if (!item) return null;
  const shot = item.shot;
  const heading = item.role === 'film'
    ? 'Full film'
    : item.role === 'segment'
      ? (item.label || item.name)
      : `Shot ${item.index ?? ''}`;
  const selectedTake = item.takes?.find((take) => take.id === item.selectedTakeId);
  const finalTake = item.role === 'clip' && item.status === 'ready' && !lockStatus && !item.excluded
    ? videoProjectFinalTake(item, lockedTakeId) : null;
  const clock = Number.isFinite(mediaDuration) && mediaDuration > 0
    ? formatClock(mediaDuration)
    : shot?.duration;
  const meta = [
    item.role === 'film' ? 'Assembled cut' : statusLabel(item.status),
    selectedTake?.takeLabel,
    clock,
    item.size ? formatFileSize(item.size) : null,
  ].filter(Boolean);
  const canRedo = item.role === 'clip' && item.status === 'ready' && Number.isFinite(item.index);

  return (
    <View style={[styles.inspector, compact && { paddingHorizontal: 0 }]}>
      <View style={styles.inspectorHead}>
        <Text size="s" weight="semibold">{heading}</Text>
        <Text size="xxs" color="tertiary">{meta.join(' · ')}</Text>
        {item.excluded ? <Text size="xs" weight="semibold" color="secondary">Excluded from final video</Text> : null}
        {finalTake ? <View
          accessible
          accessibilityLabel={`Final video: ${finalTake.takeLabel}`}
          accessibilityHint={`${finalTake.id === lockedTakeId ? 'Your selected take' : 'Latest take, used by default'}${selectedTake?.id !== finalTake.id ? `. Previewing ${selectedTake?.takeLabel || 'another take'} only` : ''}`}
          style={styles.finalTakeSummary}
        >
          <Icon name="check-circle" size={13} color={READY} />
          <Text size="xxs" weight="semibold" style={{ color: READY }}>Final · {finalTake.takeLabel}</Text>
        </View> : null}
      </View>
      <View style={styles.inspectorActions}>
      <StudioDownloadChip
        label={`Download ${heading}`}
        disabled={!canDownload}
        colors={colors}
        onPress={onDownload}
      />
      {canRedo && onRedo ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Redo shot ${item.index}`}
          disabled={redoBusy || editingLocked}
          onPress={() => onRedo(item)}
          style={({ hovered, pressed }) => [
            styles.actionChip,
            { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_secondary },
            (hovered || pressed) && { backgroundColor: colors.solid_color_tertiary },
            (redoBusy || editingLocked) && { opacity: 0.55 },
          ]}
        >
          <Icon name="arrow-rotate-right" size={11} color={colors.text_color_secondary} />
          <Text size="xxs" weight="medium" color="secondary">
            {redoBusy ? 'Sending…' : 'Improve this shot'}
          </Text>
        </Pressable>
      ) : null}
      {onToggleExcluded && item.role === 'clip' ? <Pressable accessibilityRole="button"
        accessibilityLabel={item.excluded ? 'Include again' : 'Exclude from final video'}
        disabled={redoBusy || choicesBusy || editingLocked} onPress={onToggleExcluded}
        style={[styles.actionChip, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_secondary, opacity: redoBusy || choicesBusy || editingLocked ? 0.5 : 1 }]}>
        <Icon name={item.excluded ? 'plus' : 'minus'} size={11} color={colors.text_color_secondary} />
        <Text size="xxs" weight="medium" color="secondary">{item.excluded ? 'Include again' : 'Exclude from final video'}</Text>
      </Pressable> : null}
      </View>
      <TakePicker
        item={item}
        editingLocked={editingLocked}
        colors={colors}
        onSelect={onSelectTake}
        onLock={item.excluded ? null : onLockTake}
        lockedTakeId={lockedTakeId}
        resolveUrl={resolveUrl}
        lockKind={item.role === 'clip' && item.status === 'ready' ? 'clip' : null}
        lockBusy={redoBusy || choicesBusy}
        lockStatus={lockStatus}
      />
    </View>
  );
}

export default function VideoProjectViewer({
  file,
  artifacts,
  files,
  scriptText,
  taskId,
  conversationId,
  selectedId,
  selectionRequest,
  events: eventsProp,
  onActiveMediaChange,
}) {
  const { colors } = useTheme();
  const [viewerSize, setViewerSize] = useState({ width: 0, height: 0 });
  const [imageCategory, setImageCategory] = useState('scenes');
  const scopeKey = `${taskId || ''}:${conversationId || ''}`;
  const activeScopeRef = useRef(scopeKey);
  const mountedRef = useRef(true);
  activeScopeRef.current = scopeKey;
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  const scopeIsCurrent = useCallback(() => mountedRef.current && activeScopeRef.current === scopeKey, [scopeKey]);
  const taskStatus = useCoworkerStore((state) => taskId ? state.tasks?.[taskId]?.status : null);
  const storeScript = useUIStore((state) => state.videoProjectContext?.scriptText || '');
  const resolvedScript = scriptText || storeScript || '';
  const storeEvents = useCoworkerStore((state) => (
    taskId ? state.eventsByTask?.[taskId] : null
  ));
  const events = eventsProp || storeEvents || EMPTY_EVENTS;
  const textVideoStarted = events.some((event) => event?.type === 'tool_execution_start'
    && (event.toolName || event.name) === 'generate_video');
  const transcriptEvents = useCoworkerStore((state) => (
    taskId ? state.transcriptEventsByTask?.[taskId] : null
  ));
  const workflowEvents = useMemo(() => [...(transcriptEvents || []), ...events]
    .sort((a, b) => (a.seq || 0) - (b.seq || 0))
    .filter((event, index, list) => !index || event.seq == null || event.seq !== list[index - 1].seq), [events, transcriptEvents]);
  const stillsApprovedFromEvents = useMemo(
    () => videoProjectStillsApprovedFromEvents(workflowEvents),
    [workflowEvents],
  );
  const clipGenerationInFlight = useMemo(() => {
    const pending = new Map();
    for (const event of workflowEvents) {
      if (['session_start', 'session_resume', 'session_end'].includes(event.type)) {
        pending.clear();
        continue;
      }
      const name = event.toolName || event.name || event.tool;
      if (name !== 'image_to_video' && name !== 'generate_video') continue;
      if (event.type === 'tool_execution_start') pending.set(name, (pending.get(name) || 0) + 1);
      if (event.type === 'tool_execution_end') pending.set(name, Math.max(0, (pending.get(name) || 0) - 1));
    }
    return [...pending.values()].some(count => count > 0);
  }, [workflowEvents]);
  const [stillsApprovedLocal, setStillsApprovedLocal] = useState(false);
  useEffect(() => {
    setStillsApprovedLocal(false);
  }, [taskId]);
  const baseProject = useMemo(
    () => buildVideoProject({ file, artifacts, files, scriptText: resolvedScript, selectedId }),
    [file, artifacts, files, resolvedScript, selectedId],
  );
  const referenceAudio = useMemo(() => collectVideoReferenceAudio({ file, artifacts, files }), [file, artifacts, files]);
  const [takeByGroup, setTakeByGroup] = useState({});
  const openedMediaRef = useRef(null);
  useEffect(() => { setTakeByGroup({}); openedMediaRef.current = null; }, [taskId]);
  const [remoteTimeline, setRemoteTimeline] = useState(null);
  const takeChoiceEvents = useMemo(() => (
    [
      ...(remoteTimeline && remoteTimeline.taskId === taskId ? remoteTimeline.takeChoices || [] : []),
      ...(transcriptEvents || []),
      ...(eventsProp || events),
    ].sort((a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0))
  ), [eventsProp, transcriptEvents, events, remoteTimeline, taskId]);
  const recoveredPicks = useMemo(() => videoTakePicksFromEvents(takeChoiceEvents, baseProject), [takeChoiceEvents, baseProject]);
  const takeChoiceSequence = useMemo(() => takeChoiceEvents.reduce((latest, event) => (
    Number.isFinite(event?.seq) ? Math.max(latest, event.seq) : latest
  ), 0), [takeChoiceEvents]);
  const savedOrder = useUIStore((state) => state.videoTimelineOrders?.[taskId]);
  const recoveredOrder = useMemo(() => videoTimelineOrderFromEvents(
    eventsProp || transcriptEvents || events,
  ), [eventsProp, transcriptEvents, events]);
  const [draftOrder, setDraftOrder] = useState(null);
  const [draftExclusions, setDraftExclusions] = useState(null);
  useEffect(() => { setDraftExclusions(null); }, [taskId]);
  useEffect(() => { setDraftOrder(null); }, [taskId]);
  const timelineOrder = draftOrder && draftOrder.taskId === taskId ? draftOrder.order : savedOrder || recoveredOrder;
  const [timelineLoadAttempt, setTimelineLoadAttempt] = useState(0);
  useEffect(() => {
    if (!taskId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await timelineUpdates.get(taskId)?.catch(() => {});
        const timeline = await coworkerApi.fetchVideoTimeline(taskId);
        if (cancelled) return;
        setRemoteTimeline({ taskId, order: timeline?.order || timeline?.clips?.map((clip) => clip.id) || null,
          excludedIds: timeline?.excludedClips?.map(clip => clip.id) || [], takeChoices: timeline?.takeChoices || [] });
      } catch (_) {
        if (!cancelled) setRemoteTimeline({ taskId, error: true });
      }
    })();
    return () => { cancelled = true; };
  }, [taskId, timelineLoadAttempt]);
  const timelineUnavailable = !!taskId && (!remoteTimeline || remoteTimeline.taskId !== taskId || remoteTimeline.error);
  const draggedClip = useRef(null);
  const takeInventory = useMemo(() => (
    [
      ...baseProject.clips.map((clip) => `${clip.id}:${(clip.takes || []).map((take) => take.id).join(',')}`),
      ...baseProject.stills.map((still) => `${still.id}:${(still.takes || []).map((take) => take.id).join(',')}`),
    ].join('|')
  ), [baseProject.clips, baseProject.stills]);

  useEffect(() => {
    setTakeByGroup((prev) => {
      const next = { ...prev };
      let changed = false;
      const remember = (item) => {
        const takes = item.takes || [];
        if (!takes.length) return;
        const newest = takes[takes.length - 1];
        const lockKey = `${item.id}:lock`;
        const recoveredTake = recoveredPicks[item.id]?.takeId;
        if (recoveredTake && prev[lockKey] !== recoveredTake) {
          if (openedMediaRef.current?.groupId !== item.id) next[item.id] = recoveredTake;
          next[lockKey] = recoveredTake;
          changed = true;
        }
        const seenKey = `${item.id}:seen`;
        const seen = new Set(String(prev[seenKey] || '').split('|').filter(Boolean));
        const arrived = takes.filter((take) => !seen.has(take.id));
        if (arrived.length) {
          next[item.id] = openedMediaRef.current?.groupId === item.id
            ? openedMediaRef.current.takeId
            : recoveredPicks[item.id]?.takeId || newest.id;
          next[seenKey] = takes.map((take) => take.id).join('|');
          changed = true;
        } else if (!next[seenKey]) {
          next[seenKey] = takes.map((take) => take.id).join('|');
          changed = true;
        }
      };
      baseProject.clips.forEach(remember);
      baseProject.stills.forEach(remember);
      return changed ? next : prev;
    });
  }, [takeInventory, baseProject.clips, baseProject.stills, recoveredPicks]);

  const project = useMemo(() => {
    const stills = baseProject.stills.map((still) => {
      const next = applyVideoProjectTake(still, takeByGroup[still.id]);
      if (next.shot || !Number.isFinite(next.index)) return next;
      const clip = baseProject.clips.find((entry) => entry.index === next.index);
      return clip?.shot ? { ...next, shot: clip.shot } : next;
    });
    const stillById = new Map(stills.map((still) => [still.id, still]));
    const withStill = (item) => {
      const next = applyVideoProjectTake(item, takeByGroup[item.id]);
      if (!item.still) return next;
      return { ...next, still: stillById.get(item.still.id) || applyVideoProjectTake(item.still, takeByGroup[item.still.id]) };
    };
    const excludedIds = new Set(draftExclusions && draftExclusions.taskId === taskId ? draftExclusions.ids : remoteTimeline && remoteTimeline.taskId === taskId ? remoteTimeline.excludedIds || [] : []);
    const clips = orderVideoClips(baseProject.clips.map(item => ({ ...withStill(item), excluded: excludedIds.has(item.id) })), draftOrder && draftOrder.taskId === taskId ? draftOrder.order : (remoteTimeline && remoteTimeline.taskId === taskId && remoteTimeline.order) || timelineOrder);
    const clipIds = new Set(baseProject.clips.map((clip) => clip.id));
    const playable = [...baseProject.playable.filter((item) => !clipIds.has(item.id)), ...clips];
    return { ...baseProject, clips, stills, playable };
  }, [baseProject, takeByGroup, timelineOrder, remoteTimeline, taskId, draftOrder, draftExclusions]);
  const [lockedByGroup, setLockedByGroup] = useState({});
  const effectiveLocks = useMemo(() => {
    const locks = Object.fromEntries(Object.entries(recoveredPicks).map(([key, pick]) => [key, pick.takeId]));
    for (const [key, local] of Object.entries(lockedByGroup)) {
      const recovered = recoveredPicks[key];
      // Local choices remain visible while their durable event is in flight.
      // Once a later user choice arrives, its server sequence wins over the
      // local cache, including when the choice was made in chat or another tab.
      const newer = recovered && (Number.isFinite(recovered.seq)
        ? recovered.seq > local.afterSeq
        : recovered.takeId !== local.previousTakeId);
      if (!newer) locks[key] = local.takeId;
    }
    return locks;
  }, [recoveredPicks, lockedByGroup]);
  useEffect(() => {
    setLockedByGroup({});
  }, [taskId]);
  const { urls, recoverUrl } = useResolvedUrls(project, taskId);
  const [activeId, setActiveId] = useState(project.selectedId);
  const initialWorkspaceTab = project.film ? 'final' : project.readyCount || (textVideoStarted && !project.stills.length) ? 'clips' : 'images';
  const [workspaceTab, setWorkspaceTab] = useState(initialWorkspaceTab);
  const workspaceTaskRef = useRef(scopeKey);
  useEffect(() => {
    if (workspaceTaskRef.current === scopeKey) return;
    workspaceTaskRef.current = scopeKey;
    setWorkspaceTab(initialWorkspaceTab);
  }, [scopeKey, initialWorkspaceTab]);
  const textVideoStartedRef = useRef(textVideoStarted);
  useEffect(() => {
    const started = textVideoStarted && !textVideoStartedRef.current;
    textVideoStartedRef.current = textVideoStarted;
    if (started && !project.film && !project.stills.length) setWorkspaceTab('clips');
  }, [textVideoStarted, project.film, project.stills.length]);
  const [redoTarget, setRedoTarget] = useState(null);
  const [stageStillId, setStageStillId] = useState(() => (
    project.stills.length && !project.film && !project.clips.some((clip) => clip.status === 'ready')
      ? project.stills[project.stills.length - 1].id
      : null
  ));
  const [durationsByMedia, setDurationsByMedia] = useState({});
  const playableIds = project.playable.map((item) => item.id).join('|');
  const lastFinalRef = useRef(project.film?.id);
  useEffect(() => {
    const previous = lastFinalRef.current;
    lastFinalRef.current = project.film?.id;
    if (!project.film?.id || previous === project.film.id) return;
    setActiveId(project.film.id);
    setStageStillId(null);
    setWorkspaceTab('final');
  }, [project.film?.id]);

  useEffect(() => {
    setActiveId((current) => {
      if (current && playableIds.split('|').includes(current)) return current;
      return project.selectedId;
    });
  }, [project.selectedId, playableIds]);

  useEffect(() => {
    if (project.film || project.clips.some((clip) => clip.status === 'ready')) return;
    setStageStillId((current) => {
      if (current && project.stills.some((item) => item.id === current)) return current;
      return project.stills[project.stills.length - 1]?.id || null;
    });
  }, [project.stills, project.clips, project.film]);

  const sceneCount = project.keyframes.length;
  const scenesComplete = project.clipCount > 0 && sceneCount >= project.clipCount;
  const allClipsReady = project.clipCount > 0 && project.readyCount >= project.clipCount;
  const includedClips = project.clips.filter(clip => !clip.excluded);
  const includedClipsReady = includedClips.length > 0 && includedClips.every(clip => clip.status === 'ready');
  const generationProgressRef = useRef({ scopeKey, sceneCount, scenesComplete, readyCount: project.readyCount, allClipsReady });
  useEffect(() => {
    const previous = generationProgressRef.current;
    generationProgressRef.current = { scopeKey, sceneCount, scenesComplete, readyCount: project.readyCount, allClipsReady };
    if (previous.scopeKey !== scopeKey || project.film) return;

    // Surface the first results and the completed batch. Ordinary refreshes
    // and intermediate arrivals preserve the user's browsing and take picks.
    const firstClipArrived = !previous.readyCount && project.readyCount > 0;
    const clipsFinished = !previous.allClipsReady && allClipsReady && project.readyCount > previous.readyCount;
    if (firstClipArrived || clipsFinished) {
      setActiveId(project.clips.find((clip) => clip.status === 'ready')?.id);
      setStageStillId(null);
      setWorkspaceTab('clips');
      return;
    }

    const firstSceneArrived = !previous.sceneCount && sceneCount > 0;
    const scenesFinished = !previous.scenesComplete && scenesComplete && sceneCount > previous.sceneCount;
    if (!project.readyCount && (firstSceneArrived || scenesFinished)) {
      setStageStillId(project.keyframes[0]?.id);
      setImageCategory('scenes');
      setWorkspaceTab('images');
    }
  }, [scopeKey, sceneCount, scenesComplete, allClipsReady, project.readyCount, project.film, project.clips, project.keyframes]);

  // Opening another card updates props on the existing studio. Preserve
  // timeline clicks during ordinary refreshes, but honor a new file request.
  // Uploaded File ids can arrive after the next export. That is metadata
  // hydration, not a new click on the old card. Explicit repeated clicks are
  // represented by selectionRequest. With that token, a refreshed file prop
  // is metadata, not navigation. Legacy callers without a token use the file.
  const openedFileKey = mediaBaseName(file) || file?.path || file?.id || file?._id || '';
  const openedMediaKey = `${scopeKey}|${selectionRequest > 0 ? `request:${selectionRequest}` : `file:${file ? openedFileKey : selectedId || ''}`}`;
  useEffect(() => {
    if (!file || openedMediaRef.current?.key === openedMediaKey) return;
    const name = mediaBaseName(file).toLowerCase();
    const matches = (item) => name && mediaBaseName(item).toLowerCase() === name;
    const still = baseProject.stills.find(item => matches(item) || item.takes?.some(matches));
    const item = still || baseProject.playable.find(item => matches(item) || item.takes?.some(matches));
    if (!item) return; // The requested file may arrive with a later history page.
    const take = item.takes?.find(matches);
    openedMediaRef.current = { key: openedMediaKey, groupId: item.id, takeId: take?.id };
    setStageStillId(still ? item.id : null);
    setWorkspaceTab(still ? 'images' : item.role === 'film' ? 'final' : 'clips');
    if (!still) setActiveId(item.id);
    if (take) setTakeByGroup(prev => ({ ...prev, [item.id]: take.id }));
  }, [openedMediaKey, file, baseProject]);

  const active = useMemo(
    () => project.playable.find((item) => item.id === activeId) || project.playable[0] || null,
    [project.playable, activeId],
  );
  const stageStill = useMemo(
    () => (stageStillId ? project.stills.find((item) => item.id === stageStillId) : null),
    [project.stills, stageStillId],
  );

  const videoUrl = resolveMediaUrl(active, urls);
  const posterUrl = resolveMediaUrl(active?.still, urls);
  const stillUrl = resolveMediaUrl(stageStill, urls);
  // A new take can arrive without changing the active shot. Scope browser
  // metadata to the selected media and URL, including late events from a
  // previous player, so its duration never labels the replacement clip.
  const durationKey = `${active?.mediaId || active?.selectedTakeId || active?.id || ''}:${videoUrl}`;
  const duration = durationsByMedia[durationKey];
  const rememberDuration = useCallback((seconds) => {
    setDurationsByMedia((previous) => previous[durationKey] === seconds
      ? previous
      : { ...previous, [durationKey]: seconds });
  }, [durationKey]);

  const summary = [
    project.clipCount ? `${project.clipCount} shot${project.clipCount === 1 ? '' : 's'}` : null,
    project.readyCount ? `${project.readyCount} ready` : null,
  ].filter(Boolean);

  const selectClip = useCallback((item) => {
    openedMediaRef.current = { ...openedMediaRef.current, key: openedMediaKey };
    setActiveId(item.id);
    setStageStillId(null);
    setWorkspaceTab(item.role === 'film' ? 'final' : 'clips');
  }, [openedMediaKey]);

  const selectStill = useCallback((item) => {
    openedMediaRef.current = { ...openedMediaRef.current, key: openedMediaKey };
    setStageStillId(item.id);
    setWorkspaceTab('images');
    if (Number.isFinite(item.index)) {
      const clip = project.clips.find((entry) => entry.index === item.index);
      if (clip) setActiveId(clip.id);
    }
  }, [project.clips, openedMediaKey]);
  const { addToast } = useToastStore((s) => s.actions);
  const [directionBusy, setDirectionBusy] = useState(false);
  const directionPendingRef = useRef(false);
  const [stitchRequested, setStitchRequested] = useState(false);
  const [editDirty, setEditDirty] = useState(false);
  const [changeRequest, setChangeRequest] = useState('');
  const stitchPendingFromEvents = useMemo(() => videoProjectStitchPendingFromEvents(workflowEvents), [workflowEvents]);
  const stitchApprovalKey = JSON.stringify(workflowEvents.filter(event => ['user_message', 'steer', 'user_question_resolved'].includes(event.type)).slice(-1));
  const [completedStitchKey, setCompletedStitchKey] = useState(null);
  const previousStitchFilm = useRef(project.film?.id);
  useEffect(() => {
    if (project.film?.id && previousStitchFilm.current !== project.film.id) setCompletedStitchKey(stitchApprovalKey);
    previousStitchFilm.current = project.film?.id;
  }, [project.film?.id, stitchApprovalKey]);
  useEffect(() => { setCompletedStitchKey(null); }, [scopeKey]);
  const generationEnded = taskStatus === 'completed' || taskStatus === 'cancelled' || taskStatus === 'canceled' || isWorkResumable(taskStatus);
  const stitchInProgress = !generationEnded && (stitchRequested || (stitchPendingFromEvents && completedStitchKey !== stitchApprovalKey));
  const stitchInProgressRef = useRef(stitchInProgress);
  stitchInProgressRef.current = stitchInProgress;
  const studioBusy = directionBusy || stitchInProgress;
  const canReviewClips = includedClipsReady && !project.film && !stitchInProgress && !stitchRequested;
  useEffect(() => { setStitchRequested(false); setEditDirty(false); setChangeRequest(''); }, [scopeKey, project.film?.id]);
  useEffect(() => { setRedoTarget(null); setDirectionBusy(false); }, [scopeKey]);
  const canReviewStills = videoProjectCanReviewStills(project, { events })
    && !stillsApprovedLocal
    && !stillsApprovedFromEvents;
  const referencesLocked = stitchInProgress || (!generationEnded && (clipGenerationInFlight
    || (!allClipsReady && (stillsApprovedLocal || stillsApprovedFromEvents || project.readyCount > 0))));
  const referencesLockedRef = useRef(referencesLocked);
  referencesLockedRef.current = referencesLocked;
  useEffect(() => {
    if (referencesLocked) setRedoTarget(current => current?.kind === 'still' ? null : current);
  }, [referencesLocked]);
  useEffect(() => { if (stitchInProgress) setRedoTarget(null); }, [stitchInProgress]);

  const sendDirection = useCallback(async (text, okMessage, referenceEdit = false) => {
    if (!taskId || directionBusy || stitchInProgressRef.current || directionPendingRef.current || (referenceEdit && referencesLockedRef.current)) return false;
    directionPendingRef.current = true;
    setDirectionBusy(true);
    try {
      await timelineUpdates.get(taskId)?.catch(() => {});
      if (!scopeIsCurrent() || stitchInProgressRef.current || (referenceEdit && referencesLockedRef.current)) return false;
      const sent = await sendStudioDirection(taskId, text);
      if (!sent || !scopeIsCurrent()) return false;
      if (okMessage) addToast(okMessage, 'success');
      return true;
    } catch (err) {
      if (scopeIsCurrent()) reportWorkLaunchError(err, { source: 'work_video_changes', fallback: 'Could not send that to Work' });
      return false;
    } finally {
      directionPendingRef.current = false;
      if (scopeIsCurrent()) setDirectionBusy(false);
    }
  }, [addToast, directionBusy, taskId, scopeIsCurrent]);

  const selectTake = useCallback((item, takeId) => {
    if (!item?.id || !takeId) return;
    setTakeByGroup((prev) => ({ ...prev, [item.id]: takeId }));
    if (item.role === 'clip') {
      setActiveId(item.id);
      setStageStillId(null);
    } else {
      setStageStillId(item.id);
    }
  }, []);

  const lockTakeForStitch = useCallback(async (item, takeId) => {
    if (!item?.id || !takeId || !taskId) return;
    const next = applyVideoProjectTake(item, takeId);
    const take = next.takes?.find((entry) => entry.id === takeId);
    const text = buildVideoTakeChoiceDirection(next);
    const localChoice = { takeId, afterSeq: takeChoiceSequence, previousTakeId: recoveredPicks[item.id]?.takeId };
    if (!text || !await sendDirection(text, `${take?.takeLabel || 'This take'} is locked for the stitch`)) return;
    setLockedByGroup((prev) => ({ ...prev, [item.id]: localChoice }));
    setEditDirty(true);
    setStitchRequested(false);
    useUIStore.getState().setVideoTakePick(item.id, {
      kind: 'clip',
      index: item.index,
      file: take?.name || take?.path || next.name,
      label: take?.takeLabel,
    });
  }, [sendDirection, taskId, takeChoiceSequence, recoveredPicks]);

  const lockTakeForStill = useCallback(async (item, takeId) => {
    if (!item?.id || !takeId || !taskId || referencesLockedRef.current) return;
    const next = applyVideoProjectTake(item, takeId);
    const take = next.takes?.find((entry) => entry.id === takeId);
    const text = buildVideoStillTakeChoiceDirection(next);
    const localChoice = { takeId, afterSeq: takeChoiceSequence, previousTakeId: recoveredPicks[item.id]?.takeId };
    if (!text || !await sendDirection(text, `${take?.takeLabel || 'This take'} is locked as the ${stillLockNoun(item)}`, true)) return;
    setLockedByGroup((prev) => ({ ...prev, [item.id]: localChoice }));
    useUIStore.getState().setVideoTakePick(item.id, {
      kind: 'still',
      role: item.kind,
      character: item.character,
      index: item.index,
      file: take?.name || take?.path || next.name,
      label: take?.takeLabel,
    });
  }, [sendDirection, taskId, takeChoiceSequence, recoveredPicks]);

  const redoShot = useCallback((item) => {
    if (!Number.isFinite(item?.index) || stitchInProgressRef.current) return;
    setRedoTarget({ kind: 'clip', item });
  }, []);

  const redoStill = useCallback((item) => {
    if (!item || referencesLockedRef.current) return null;
    const shot = item.shot || (Number.isFinite(item.index)
      ? project.clips.find((clip) => clip.index === item.index)?.shot
      : null);
    setRedoTarget({ kind: 'still', item: shot && !item.shot ? { ...item, shot } : item });
    return null;
  }, [project.clips]);

  const submitRedo = useCallback(async ({ prompt, notes }) => {
    if (!redoTarget) return;
    const { kind, item } = redoTarget;
    const text = buildVideoRedoDirection({ item, prompt, notes, kind });
    const toast = kind === 'still'
      ? (stillRedoCopy(item)?.toast || 'Asked Work to redo that still')
      : `Asked Work to redo shot ${item.index}`;
    const sent = await sendDirection(text, toast, kind === 'still');
    if (sent) setRedoTarget(null);
    return sent;
  }, [redoTarget, sendDirection]);

  const approveStills = useCallback(async () => {
    if (!canReviewStills || timelineUnavailable || referencesLockedRef.current) return false;
    const picks = {};
    for (const still of baseProject.stills) {
      const take = videoProjectFinalTake(still, effectiveLocks[still.id]);
      if (take) picks[still.id] = {
        kind: 'still', role: still.kind, character: still.character, index: still.index,
        file: take.name || take.path, label: take.takeLabel,
      };
    }
    const sent = await sendDirection(
      buildVideoStillsDirectionFromPicks(picks, { freezeReferences: true }),
      'Asked Work to animate the approved images',
      true,
    );
    if (sent) setStillsApprovedLocal(true);
    return sent;
  }, [sendDirection, baseProject.stills, effectiveLocks, canReviewStills, timelineUnavailable]);

  const stitchPicks = useCallback(() => {
    const picks = {};
    for (const clip of baseProject.clips) {
      const takeId = effectiveLocks[clip.id];
      const take = clip.takes?.find((entry) => entry.id === takeId);
      if (take) picks[clip.id] = { file: take.path || take.name };
    }
    return picks;
  }, [baseProject.clips, effectiveLocks]);

  const [timelineSync, setTimelineSync] = useState('');
  const syncRevision = useRef(0);
  useEffect(() => {
    syncRevision.current += 1;
    setTimelineSync('');
  }, [taskId]);

  const syncOrder = useCallback(async (clips) => {
    if (!taskId) return;
    const revision = ++syncRevision.current;
    setTimelineSync('saving');
    try {
      await queueTimelineUpdate(taskId, clips);
      if (syncRevision.current === revision) setTimelineSync('saved');
    } catch (_) {
      if (syncRevision.current === revision) setTimelineSync('error');
    }
  }, [taskId]);

  const moveClip = useCallback((id, targetId) => {
    if (directionBusy || stitchInProgressRef.current || timelineUnavailable || id === targetId) return;
    const order = project.clips.map((clip) => clip.id);
    const from = order.indexOf(id);
    const to = order.indexOf(targetId);
    if (from < 0 || to < 0) return;
    order.splice(to, 0, ...order.splice(from, 1));
    setDraftOrder({ taskId, order });
    setEditDirty(true);
    setStitchRequested(false);
    if (taskId) {
      useUIStore.getState().setVideoTimelineOrder(taskId, order);
      void syncOrder(orderVideoClips(project.clips, order));
    }
  }, [project.clips, taskId, directionBusy, timelineUnavailable, syncOrder]);

  const toggleClipExcluded = useCallback((id) => {
    if (directionBusy || stitchInProgressRef.current || timelineUnavailable || !project.clips.some(clip => clip.id === id)) return;
    const clips = project.clips.map(clip => clip.id === id ? { ...clip, excluded: !clip.excluded } : clip);
    setDraftExclusions({ taskId, ids: clips.filter(clip => clip.excluded).map(clip => clip.id) });
    setEditDirty(true);
    setStitchRequested(false);
    void syncOrder(clips);
  }, [project.clips, taskId, directionBusy, timelineUnavailable, syncOrder]);

  const approveStitch = useCallback(async () => {
    if (directionBusy || stitchInProgressRef.current || timelineUnavailable || !includedClipsReady || directionPendingRef.current) return;
    directionPendingRef.current = true;
    setDirectionBusy(true);
    try {
      // Flush the same visible snapshot before granting approval. A failed save
      // must not start a render against stale workspace state.
      const timeline = await queueTimelineUpdate(taskId, project.clips);
      if (!scopeIsCurrent()) return;
      setTimelineSync('saved');
      await sendStudioDirection(taskId,
        buildVideoTimelineDirection(project.clips, stitchPicks(), { approve: true })
        + `\nRead video-timeline.json before rendering; saved revision ${timeline.revision}. The saved project state is authoritative if it changes again.`,
        // Saving the cut invalidates any older review card. This explicit
        // studio click approves the just-saved cut as a new user direction.
        { freshDirection: true });
      if (!scopeIsCurrent()) return;
      setStitchRequested(true);
      setEditDirty(false);
      addToast('Creating your final video', 'success');
    } catch (error) {
      if (!scopeIsCurrent()) return;
      setTimelineSync('error');
      reportWorkLaunchError(error, { source: 'work_video_stitch', fallback: 'Could not save the timeline and start stitching' });
    } finally {
      directionPendingRef.current = false;
      if (scopeIsCurrent()) setDirectionBusy(false);
    }
  }, [project.clips, stitchPicks, taskId, directionBusy, timelineUnavailable, includedClipsReady, addToast, scopeIsCurrent]);

  const resolveTakeUrl = useCallback((take) => (
    take ? (urls[take.id] || itemDirectUrl(take)) : ''
  ), [urls]);

  const downloadItem = useCallback(async (item, { still = false } = {}) => {
    const media = item?.status === 'still' && item.still ? item.still : item;
    const url = resolveMediaUrl(media, urls);
    if (!url || !media) return;
    const filename = itemDownloadName(media, { still: still || media !== item });
    try {
      const result = await downloadMedia(url, filename);
      if (result?.success === false && !result.cancelled && !result.canceled) {
        addToast(result.message || 'Could not download that file', 'error');
      }
    } catch (err) {
      addToast(err?.message || 'Could not download that file', 'error');
    }
  }, [addToast, urls]);

  const activeDownload = useMemo(() => {
    if (stageStill) {
      const url = resolveMediaUrl(stageStill, urls);
      return url
        ? { url, name: itemDownloadName(stageStill, { still: true }), kind: 'image' }
        : null;
    }
    if (workspaceTab === 'images' || (workspaceTab === 'final' && !project.film)) return null;
    const media = active?.status === 'still' ? active.still : active;
    const url = resolveMediaUrl(media, urls);
    return url
      ? { url, name: itemDownloadName(media), kind: media === active ? 'video' : 'image' }
      : null;
  }, [active, stageStill, urls, workspaceTab, project.film]);

  useEffect(() => {
    onActiveMediaChange?.(activeDownload);
  }, [activeDownload, onActiveMediaChange]);

  const navigateWorkspace = (tab) => {
    // Browsing also cancels an old file-open request whose media has not
    // arrived yet; a later history refresh must not replay it over this tab.
    openedMediaRef.current = { ...openedMediaRef.current, key: openedMediaKey };
    setWorkspaceTab(tab);
    if (tab === 'images') setStageStillId(stageStill?.id || project.keyframes?.[0]?.id || project.stills[0]?.id || null);
    else {
      setStageStillId(null);
      setActiveId(tab === 'final' ? project.film?.id : project.clips.find((clip) => clip.id === activeId)?.id || project.clips[0]?.id || project.segments?.[0]?.id);
    }
  };
  const currentFinal = active?.role === 'film' ? active : project.film;
  const viewingFinal = workspaceTab === 'final';
  const viewingImages = workspaceTab === 'images';
  const viewingClips = workspaceTab === 'clips';
  const denseClips = viewingClips && viewerSize.height > 0 && viewerSize.height < 680;
  const nextReviewTab = project.readyCount > 0 ? 'clips' : 'images';
  const nextReviewLabel = nextReviewTab === 'clips' ? 'Review clips' : 'Review reference media';
  const stageAvailable = viewingImages ? !!stageStill : viewingFinal ? !!project.film : !!active && active.role !== 'film';
  const splitWorkspace = viewingClips || viewerSize.width >= 640;
  const previewWidth = splitWorkspace ? (viewerSize.width - 48) * 0.54 : (viewerSize.width || 600) - 32;
  const availableHeight = Math.max(220, (viewerSize.height || 700) - 210);
  const previewHeight = Math.min(previewWidth * 9 / 16, splitWorkspace ? Math.max(140, availableHeight * 0.5) : Math.min(145, availableHeight * 0.24));
  const stageStillKind = stageStill?.kind;
  const selectedStillId = stageStill?.id;
  useEffect(() => {
    if (!selectedStillId) return;
    setImageCategory(stageStillKind === 'keyframe' ? 'scenes' : ['master', 'sheet'].includes(stageStillKind) ? 'characters' : 'other');
  }, [selectedStillId, stageStillKind]);
  const requestVideoChange = async () => {
    if (!currentFinal || !changeRequest.trim()) return;
    if (await sendDirection(`Revise the finished video ${currentFinal?.name || ''}. ${changeRequest.trim()}\nKeep the existing export and all source clips. Export the revision as a new file.`, 'Your video changes were sent')) setChangeRequest('');
  };

  const stageTitle = stageStill
    ? displayStillLabel(stageStill)
    : active?.role === 'film'
      ? (active.id === project.film?.id ? 'Latest export' : 'Earlier export')
      : active
        ? (Number.isFinite(active.index) ? `Shot ${active.index}` : active.label || active.name)
        : 'Video';

  const imageGroups = [
    { id: 'scenes', label: 'Scenes', title: 'Scene images', description: 'The starting image for each shot. Check the look before adding motion.', items: project.stills.filter((item) => item.kind === 'keyframe') },
    { id: 'characters', label: 'Characters', title: 'Characters & references', description: 'Visual references help keep people and style consistent across shots.', items: project.stills.filter((item) => item.kind === 'master' || item.kind === 'sheet') },
    { id: 'other', label: 'Other', title: 'Other images', description: 'Logos and supporting images from this project.', items: project.stills.filter((item) => !['keyframe', 'master', 'sheet'].includes(item.kind)) },
    { id: 'audio', label: 'Audio', title: 'Reference audio', description: 'Voice, music, and sound samples help guide how your video sounds.', items: referenceAudio },
  ];
  const availableImageGroups = imageGroups.filter((group) => group.items.length || group.id === 'audio');
  const activeImageGroup = availableImageGroups.find((group) => group.id === imageCategory) || availableImageGroups[0];
  const imageLibrary = <View style={styles.libraryBody}>
    <View style={styles.libraryHead}>
      <Text size="s" weight="semibold">Reference media</Text>
      <View style={styles.libraryTabs} accessibilityRole="tablist" accessibilityLabel="Reference media categories">
        {availableImageGroups.map((group) => <Pressable key={group.id} accessibilityRole="tab"
          accessibilityLabel={`${group.label} (${group.items.length})`} aria-selected={activeImageGroup?.id === group.id}
          onPress={() => setImageCategory(group.id)} style={[styles.libraryTab, {
            backgroundColor: activeImageGroup?.id === group.id ? colors.solid_color_tertiary : 'transparent',
            borderColor: activeImageGroup?.id === group.id ? colors.border_color_primary : colors.border_color_secondary,
          }]}><Text size="xs" weight="medium">{group.label} {group.items.length}</Text></Pressable>)}
      </View>
      <Text size="xxs" color="secondary">{activeImageGroup?.description || 'Your reference media will appear here.'}</Text>
    </View>
    <StudioScrollPane colors={colors} accessibilityLabel="Reference media library" moreLabel="More media below" resetKey={`${workspaceTab}:${activeImageGroup?.id}`} contentContainerStyle={styles.libraryContent}>
      {activeImageGroup?.id === 'audio' ? <VideoProjectReferenceAudio items={referenceAudio} taskId={taskId} conversationId={conversationId} colors={colors}
        readOnly={referencesLocked || studioBusy || timelineUnavailable} /> : <View style={styles.assetGrid}>
        {activeImageGroup?.items.map((item) => {
          const chosenTake = !timelineUnavailable && item.takes?.find((take) => take.id === effectiveLocks[item.id]);
          const libraryItem = chosenTake ? applyVideoProjectTake(item, chosenTake.id) : item;
          return <View key={item.id} style={styles.gridCell}>
            <StillCard fluid item={libraryItem} chosenTake={chosenTake} selected={item.id === stageStillId} url={resolveMediaUrl(libraryItem, urls)} colors={colors}
              onPress={() => selectStill(libraryItem)} onDownload={() => downloadItem(libraryItem, { still: true })} />
          </View>;
        })}
      </View>}
    </StudioScrollPane>
  </View>;

  const timelineRail = project.clips.length + (project.segments?.length || 0) > 0 ? (
    <View style={[styles.timelineStrip, denseClips && styles.denseTimelineStrip, { borderTopColor: colors.border_color_secondary }]}>
      <View style={[styles.timelineHead, denseClips && styles.denseTimelineHead]}>
      <View style={styles.sectionHeading}><Text size="s" weight="semibold">Timeline</Text><Text size="xs" color="secondary">{project.readyCount} of {project.clipCount} clips ready · {includedClips.length} included</Text></View>
      <Text size="xxs" color="secondary">{Platform.OS === 'web'
        ? 'Select a shot to preview. Drag clips left or right, or use the reorder arrows.'
        : 'Select a shot to preview. Use the arrows to reorder clips.'}</Text>
      </View>
      <StudioTimelineRail colors={colors} accessibilityLabel="Clip timeline" resetKey={workspaceTab} contentContainerStyle={[styles.timelineContent, denseClips && styles.denseTimelineContent]}>
        {[...project.clips, ...(project.segments || [])].map((item) => {
          const position = project.clips.findIndex((clip) => clip.id === item.id);
          const reorderable = position >= 0 && project.clips.length > 1;
          const earlierDisabled = studioBusy || timelineUnavailable || position === 0;
          const laterDisabled = studioBusy || timelineUnavailable || position === project.clips.length - 1;
          const finalTake = !timelineUnavailable && item.role === 'clip' && item.status === 'ready'
            ? videoProjectFinalTake(item, effectiveLocks[item.id]) : null;
          const timelineItem = finalTake ? applyVideoProjectTake(item, finalTake.id) : item;
          const content = <View>
          <TimelineCard
            key={item.id}
            compact
            dense={denseClips}
            item={timelineItem}
            selected={!stageStill && item.id === active?.id}
            finalTake={finalTake}
            selectionPending={timelineUnavailable}
            posterUrl={resolveMediaUrl(item.still, urls)}
            videoUrl={resolveMediaUrl(timelineItem, urls)}
            colors={colors}
            onPress={() => selectClip(item)}
            onDownload={() => downloadItem(timelineItem, { still: item.status === 'still' && !resolveMediaUrl(timelineItem, urls) })}
          />
          {reorderable ? <View style={[styles.reorderControl, denseClips && styles.denseReorderControl, {
            backgroundColor: colors.solid_color_secondary,
            borderColor: colors.border_color_secondary,
          }]}>
            <Pressable accessibilityRole="button" accessibilityLabel={`Move shot ${item.index ?? item.name} earlier`}
              accessibilityState={{ disabled: earlierDisabled }}
              disabled={earlierDisabled}
              onPress={() => moveClip(item.id, project.clips[position - 1]?.id)}
              style={({ hovered, pressed }) => [styles.reorderButton,
                !earlierDisabled && (hovered || pressed) && { backgroundColor: colors.solid_color_tertiary },
                !earlierDisabled && pressed && styles.reorderButtonPressed,
                earlierDisabled && styles.reorderButtonDisabled,
              ]}>
              <Icon name="chevron-left" size={12} color={colors.text_color_secondary} />
            </Pressable>
            <View style={styles.reorderPosition}>
              <Text size="xs" weight="medium" color="secondary" style={styles.reorderNumber}>{position + 1}</Text>
            </View>
            <Pressable accessibilityRole="button" accessibilityLabel={`Move shot ${item.index ?? item.name} later`}
              accessibilityState={{ disabled: laterDisabled }}
              disabled={laterDisabled}
              onPress={() => moveClip(item.id, project.clips[position + 1]?.id)}
              style={({ hovered, pressed }) => [styles.reorderButton,
                !laterDisabled && (hovered || pressed) && { backgroundColor: colors.solid_color_tertiary },
                !laterDisabled && pressed && styles.reorderButtonPressed,
                laterDisabled && styles.reorderButtonDisabled,
              ]}>
              <Icon name="chevron-right" size={12} color={colors.text_color_secondary} />
            </Pressable>
          </View> : null}
          {item.role === 'clip' ? <Pressable accessibilityRole="button"
            accessibilityLabel={item.excluded ? `Include shot ${item.index ?? item.name} again` : `Exclude shot ${item.index ?? item.name} from final video`}
            disabled={studioBusy || timelineUnavailable} onPress={() => toggleClipExcluded(item.id)}
            style={[styles.timelineInclusion, { opacity: studioBusy || timelineUnavailable ? 0.5 : 1 }]}>
            <Icon name={item.excluded ? 'plus' : 'minus'} size={10} color={colors.text_color_secondary} />
            <Text size="xxs" color="secondary">{item.excluded ? 'Include again' : 'Exclude'}</Text>
          </Pressable> : null}
          </View>;
          return Platform.OS === 'web' && reorderable ? (
            <div key={item.id} draggable={!studioBusy && !timelineUnavailable} style={{ width: 152, flexShrink: 0, padding: 4, boxSizing: 'border-box', cursor: studioBusy ? 'default' : 'grab' }}
              onDragStart={(event) => { draggedClip.current = item.id; event.dataTransfer.setData('text/plain', item.id); }}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => { event.preventDefault(); moveClip(draggedClip.current, item.id); draggedClip.current = null; }}
              onDragEnd={() => { draggedClip.current = null; }}>
              {content}
            </div>
          ) : <View key={item.id} style={styles.timelineCell}>{content}</View>;
        })}
      </StudioTimelineRail>
    </View>
  ) : null;

  const timelineFeedback = <>
      {timelineUnavailable ? <View accessibilityLiveRegion="polite">
        <Text size="xxs" color="tertiary">{remoteTimeline?.error ? 'Could not load the saved timeline.' : 'Loading timeline…'}</Text>
        {remoteTimeline?.error ? <Pressable accessibilityRole="button" accessibilityLabel="Reload timeline"
          onPress={() => { setRemoteTimeline(null); setTimelineLoadAttempt((value) => value + 1); }}>
          <Text size="xxs">Retry</Text>
        </Pressable> : null}
      </View> : null}
      {timelineSync ? <View accessibilityLiveRegion="polite">
        <Text size="xxs" color="tertiary">
          {timelineSync === 'saving' ? 'Saving timeline…' : timelineSync === 'saved' ? 'Timeline saved' : 'Timeline not saved. Retry before stitching.'}
        </Text>
        {timelineSync === 'error' ? <Pressable accessibilityRole="button"
          accessibilityLabel="Retry timeline sync" onPress={() => syncOrder(project.clips)} style={styles.actionChip}>
          <Text size="xxs">Retry</Text>
        </Pressable> : null}
      </View> : null}
  </>;

  const steps = [
    { id: 'images', title: 'Reference media', detail: `${project.stills.length} images · ${referenceAudio.length} audio`, complete: project.readyCount > 0 || !!project.film },
    { id: 'clips', title: 'Clips', detail: `${project.readyCount}/${project.clipCount} ready`, complete: !!project.film },
    { id: 'final', title: 'Final video', detail: project.film ? 'Ready to download' : 'Export & download', complete: !!project.film },
  ].filter(step => !project.film || step.id === 'final'
    || (step.id === 'images' ? project.stills.length > 0 || referenceAudio.length > 0 : project.clipCount > 0));
  const workflowTitle = viewingFinal ? (project.film ? 'Your video is ready' : 'Bring it all together')
    : viewingImages ? 'Guide the look and sound' : 'Make every shot count';
  const workflowDescription = viewingFinal ? (project.film ? 'Watch the finished video, download it, or ask for a change.' : 'Review your clips, then combine them into one finished video.')
    : viewingImages ? 'Images and audio that guide how your video looks and sounds.'
      : 'Choose a shot from the timeline, then preview, compare, or improve it.';

  const stagePanel = (
    <View style={[styles.stageWrap, viewingClips && styles.clipStageWrap,
      viewingClips && Platform.OS === 'macos' && { flex: 1 }, viewingImages && styles.imageStageWrap]}>
          <View style={[styles.stage, viewingClips ? styles.clipStage : { height: previewHeight },
            viewingClips && Platform.OS === 'macos' && { flex: 1, aspectRatio: undefined, height: undefined }, viewingImages && styles.imageStage]}>
            {!stageAvailable ? <StageEmpty colors={colors} icon={viewingImages ? 'image' : 'film'}
              title={viewingFinal ? 'Your finished video will appear here' : viewingImages ? 'Your scene images will appear here' : 'Your clips will appear here'}
              subtitle={viewingFinal ? (nextReviewTab === 'clips' ? 'Choose Clips to review your shots and create your video.' : 'Choose Reference media to review your images and audio before creating clips.') : 'Describe your idea in the chat. Your images and clips appear as they are created.'} />
            : viewingImages ? (stillUrl ? <Image source={{ uri: stillUrl }} style={styles.stageImage} contentFit="contain" />
              : <StageEmpty colors={colors} icon="image" title="Loading this image" subtitle="The preview will appear when the image is available." />)
            : videoUrl ? <CinemaVideo key={active?.selectedTakeId || active?.id || videoUrl} url={videoUrl} name={active?.name} onDuration={rememberDuration}
              onError={() => recoverUrl(active)} onRetry={() => recoverUrl(active, { force: true })} />
            : posterUrl ? <Image source={{ uri: posterUrl }} style={styles.stageImage} contentFit="contain" />
            : <StageEmpty colors={colors} icon="clapperboard" title={active?.status === 'planned' ? `Shot ${active.index} is planned` : 'Loading this clip'} subtitle={active?.shot?.action || 'Your preview will appear when the clip is available.'} />}
          </View>
          {stageAvailable ? <View style={styles.stageCaption}>
            <Text size="xs" weight="medium" style={{ color: '#E5E7EB', flexShrink: 1 }} numberOfLines={1}>{stageTitle}</Text>
            <Text size="xxs" style={{ color: '#CBD5E1', flexShrink: 1 }} numberOfLines={1}>{viewingFinal ? [duration ? formatClock(duration) : '', active?.size ? formatFileSize(active.size) : ''].filter(Boolean).join(' · ') : viewingImages ? 'Image preview' : summary.join(' · ')}</Text>
          </View> : null}
        </View>
  );
  const inspectorPanel = (
    viewingImages && stageStill ? (
        <StillInspector
          item={stageStill}
          compact
          referencesLocked={referencesLocked}
          choicesBusy={timelineUnavailable}
          colors={colors}
          onRedo={taskId ? redoStill : null}
          onDownload={() => downloadItem(stageStill, { still: true })}
          canDownload={!!stillUrl}
          onSelectTake={(takeId) => selectTake(stageStill, takeId)}
          onLockTake={taskId ? (takeId) => lockTakeForStill(stageStill, takeId) : null}
          lockedTakeId={stageStill ? effectiveLocks[stageStill.id] : null}
          resolveUrl={resolveTakeUrl}
          redoBusy={directionBusy}
          lockStatus={timelineUnavailable ? (remoteTimeline?.error ? 'Saved take choices could not be loaded. Retry below before approving.' : 'Loading saved take choices…') : null}
        />
      ) : !viewingImages && !viewingFinal && stageAvailable ? (
        <Inspector
          item={active}
          compact
          choicesBusy={timelineUnavailable}
          colors={colors}
          onRedo={taskId ? redoShot : null}
          onDownload={() => downloadItem(active)}
          canDownload={!!videoUrl || !!posterUrl}
          onToggleExcluded={() => toggleClipExcluded(active.id)}
          editingLocked={stitchInProgress}
          onSelectTake={(takeId) => selectTake(active, takeId)}
          onLockTake={taskId ? (takeId) => lockTakeForStitch(active, takeId) : null}
          lockedTakeId={active ? effectiveLocks[active.id] : null}
          resolveUrl={resolveTakeUrl}
          redoBusy={directionBusy}
          lockStatus={timelineUnavailable ? (remoteTimeline?.error ? 'Saved take choices could not be loaded. Retry below before approving.' : 'Loading saved take choices…') : null}
          mediaDuration={duration}
        />
      ) : null
  );

  const finalDownload = project.film ? (
<View style={[styles.section, { flexShrink: 0 }]}>
            <Pressable accessibilityRole="button" accessibilityLabel="Download Full film" disabled={!resolveMediaUrl(currentFinal, urls)}
              onPress={() => downloadItem(currentFinal)} style={({ hovered }) => [styles.reviewAction, { backgroundColor: colors.text_color_primary }, hovered && { opacity: 0.9 }]}>
              <Icon name="arrow-down-to-line" size={15} color={colors.solid_color_primary} />
              <Text size="s" weight="semibold" style={{ color: colors.solid_color_primary }}>Download video</Text>
            </Pressable>
            <Text size="xxs" color="secondary" numberOfLines={1}>{currentFinal?.name}</Text>
          </View>
  ) : null;
  const finalVersions = project.finals?.length ? <View accessibilityRole={Platform.OS === 'web' ? 'region' : undefined} accessibilityLabel="Final video versions" style={styles.finalVersions}>
    <View style={styles.finalVersionsGrid}>
      {project.finals.map((item, index) => {
        const selected = currentFinal?.id === item.id;
        return <Pressable key={item.id} accessibilityRole="button" accessibilityLabel={`Preview export ${item.name}`}
          accessibilityState={{ selected }} aria-pressed={selected} onPress={() => selectClip(item)}
          style={({ hovered }) => [styles.exportCard, {
            borderColor: selected ? colors.text_color_primary : colors.border_color_secondary,
            backgroundColor: selected || hovered ? colors.solid_color_tertiary : colors.solid_color_secondary,
          }]}>
          <View style={styles.sectionHeading}>
            <Text size="xs" weight="semibold">Version {project.finals.length - index}</Text>
            {selected ? <Icon name="check" size={14} color={colors.text_color_primary} /> : <Icon name="play" size={12} color={colors.text_color_secondary} />}
          </View>
          <Text size="xxs" color="secondary" numberOfLines={1}>{item.name}</Text>
          <Text size="xxs" color={selected ? 'primary' : 'secondary'}>{selected ? 'Viewing · ' : ''}{index === 0 ? 'Latest export' : 'Earlier export'}{item.size ? ` · ${formatFileSize(item.size)}` : ''}</Text>
        </Pressable>;
      })}
    </View>
  </View> : null;
  const finalTools = project.film ? <View style={styles.libraryBody}>
    <StudioScrollPane colors={colors} accessibilityLabel="Final video tools" moreLabel="More options below" resetKey={workspaceTab} contentContainerStyle={styles.toolsContent}>
      {taskId ? <View style={styles.changeCard}>
            <Text size="s" weight="semibold">Make changes</Text>
            <Text size="xs" color="secondary">Describe a change to the version you’re viewing. Each revision is saved as a new version.</Text>
            <Text size="xxs" weight="medium" numberOfLines={1}>Editing: {currentFinal?.name}</Text>
            <View style={styles.suggestionRow}>{[
              ['Shorter cut', 'Make a 15-second version, keeping the strongest moments.'],
              ['Vertical video', 'Make a 9:16 vertical version for social media. Keep important subjects and text in frame.'],
              ['Adjust music', 'Lower the background music so the voice is easier to hear.'],
            ].map(([label, prompt]) => <Pressable key={label} accessibilityRole="button" accessibilityLabel={label} disabled={studioBusy} onPress={() => setChangeRequest(prompt)} style={[styles.actionChip, { borderColor: colors.border_color_primary }]}><Text size="xs">{label}</Text></Pressable>)}</View>
            <TextInput accessibilityLabel="Video change request" placeholder="e.g. Make the music quieter and hold the end card longer" placeholderTextColor={colors.text_color_secondary}
              value={changeRequest} onChangeText={setChangeRequest} multiline editable={!studioBusy} style={[styles.changeInput, { color: colors.text_color_primary, borderColor: colors.border_color_primary, backgroundColor: colors.solid_color_primary }]} />
            <Pressable accessibilityRole="button" accessibilityLabel="Request video changes" disabled={studioBusy || !changeRequest.trim()} onPress={requestVideoChange}
              style={[styles.reviewAction, { backgroundColor: colors.text_color_primary, opacity: studioBusy || !changeRequest.trim() ? 0.45 : 1 }]}>
              <Text size="xs" weight="semibold" style={{ color: colors.solid_color_primary }}>{studioBusy ? 'Sending…' : 'Create revised video'}</Text>
            </Pressable>
          </View> : <View style={styles.section}>
            <Text size="s" weight="semibold">Your saved versions</Text>
            <Text size="xs" color="secondary">Select a version beneath the preview to watch it, then download the one you want to keep.</Text>
          </View>}
    </StudioScrollPane>
  </View> : <View style={styles.libraryHead}>
    <Text size="s" weight="semibold">Review before exporting</Text>
    <Text size="xs" color="secondary">{nextReviewTab === 'clips'
      ? 'Compare your takes and check the clip order, then approve the final video.'
      : 'Review your scene images first, then create and compare your video clips.'}</Text>
    <Text size="xs" color="secondary">Revision tools and export history will be available after your first export.</Text>
  </View>;

  const showImagesApproved = project.stills.length > 0 && referencesLocked;
  const showWorkflowDock = (viewingImages && (canReviewStills || timelineUnavailable || showImagesApproved))
    || (!viewingImages && !viewingFinal && (canReviewClips || (project.film && includedClipsReady) || timelineUnavailable || !!timelineSync || (project.clips.length > 0 && !includedClips.length)))
    || stitchInProgress || stitchRequested || (viewingFinal && !project.film);

  return (
    <View style={[styles.root, { backgroundColor: colors.solid_color_primary }]} onLayout={(event) => {
      const { width, height } = event.nativeEvent.layout;
      setViewerSize((previous) => previous.width === width && previous.height === height ? previous : { width, height });
    }}>
      <View style={[styles.workspaceNav, denseClips && styles.denseWorkspaceNav, { borderBottomColor: colors.border_color_secondary }]} accessibilityRole="tablist" accessibilityLabel="Video creation steps">
        {steps.map((step, index) => <Pressable key={step.id} accessibilityRole="tab"
          accessibilityLabel={step.title} accessibilityState={{ selected: workspaceTab === step.id }} aria-selected={workspaceTab === step.id}
          onPress={() => navigateWorkspace(step.id)} style={({ hovered }) => [styles.workspaceTab,
            denseClips && styles.denseWorkspaceTab,
            { backgroundColor: workspaceTab === step.id ? colors.solid_color_secondary : 'transparent', borderColor: workspaceTab === step.id ? colors.border_color_primary : 'transparent' },
            hovered && { backgroundColor: colors.solid_color_secondary }]}>
          <View style={[styles.stepNumber, { backgroundColor: workspaceTab === step.id ? colors.text_color_primary : colors.solid_color_secondary }]}>
            {step.complete ? <Icon name="check" size={11} color={workspaceTab === step.id ? colors.solid_color_primary : colors.text_color_secondary} />
              : <Text size="xxs" weight="semibold" style={{ color: workspaceTab === step.id ? colors.solid_color_primary : colors.text_color_secondary }}>{index + 1}</Text>}
          </View>
          <View style={{ flex: 1, minWidth: 0 }}><Text size="xs" weight="semibold">{step.title}</Text><Text size="xxs" color="secondary">{step.detail}</Text></View>
        </Pressable>)}
      </View>

      <View style={[styles.workspaceIntro, denseClips && styles.denseWorkspaceIntro]}>
        <Text size={denseClips ? 'm' : 'l'} weight="semibold">{workflowTitle}</Text>
        <Text size={denseClips ? 'xxs' : 'xs'} color="secondary">{workflowDescription}</Text>
      </View>
      <View style={[styles.workspaceBody, !splitWorkspace && styles.workspaceStack]} testID="video-studio-workspace">
        <View style={[styles.previewColumn, !splitWorkspace && styles.previewColumnStack, viewingClips && styles.clipPreviewColumn]}>
          {viewingImages ? <>
            {stagePanel}
            <View accessibilityRole={Platform.OS === 'web' ? 'region' : undefined} accessibilityLabel="Selected media controls"
              style={[styles.inspectorContent, styles.imageInspector]}>{inspectorPanel}</View>
          </> : <>
            {stagePanel}
            {viewingFinal ? <>
              {finalDownload}
              {project.finals?.length ? <View style={[styles.sectionHeading, { flexShrink: 0 }]}>
                <Text size="s" weight="semibold">Video versions</Text>
                <Text size="xs" color="secondary">{project.finals.length} {project.finals.length === 1 ? 'version' : 'versions'} · Select to preview</Text>
              </View> : null}
              <StudioScrollPane colors={colors} accessibilityLabel="Selected media controls" moreLabel="More versions below"
                resetKey={scopeKey} style={styles.inspectorScroll} contentContainerStyle={styles.finalPreviewContent}>
                {finalVersions}
              </StudioScrollPane>
            </> : null}
          </>}
        </View>
        <View style={[styles.toolsColumn, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block }]}>
          {viewingImages ? imageLibrary : viewingFinal ? finalTools : <>
            <StudioScrollPane colors={colors} accessibilityLabel="Selected media controls" moreLabel="More shot controls"
              resetKey={active?.id} style={styles.inspectorScroll} contentContainerStyle={styles.toolsContent}>
              {inspectorPanel || <Text size="xs" color="secondary">Select a clip from the timeline to preview and improve it.</Text>}
            </StudioScrollPane>
          </>}
        </View>
      </View>
      {viewingClips ? timelineRail : null}
      {showWorkflowDock ? <View style={[styles.workflowDock, denseClips && styles.denseWorkflowDock, { borderTopColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block }]}>
        {!viewingFinal ? timelineFeedback : null}
        {viewingClips && project.clips.length > 0 && !includedClips.length ? <Text size="xs" color="secondary">Every scene is excluded. Include at least one clip to create the final video.</Text> : null}
        {viewingFinal && !project.film ? <View style={styles.dockRow}><Pressable accessibilityRole="button" accessibilityLabel={nextReviewLabel} onPress={() => navigateWorkspace(nextReviewTab)} style={[styles.actionChip, { borderColor: colors.border_color_secondary }]}><Text size="xs">{nextReviewLabel}</Text><Icon name="arrow-right" size={12} color={colors.text_color_secondary} /></Pressable></View> : null}
        {viewingImages && canReviewStills && taskId ? <ReviewBanner title="Happy with these images?" body="Your chosen image versions will become video clips. Otherwise, the latest versions are used."
          actionLabel="Approve images & create clips" actionIcon="film" accessibilityLabel="Approve stills and start animating" onAction={approveStills} busy={studioBusy || timelineUnavailable} colors={colors} /> : null}
        {!viewingFinal && !viewingImages && canReviewClips && taskId ? <ReviewBanner title="Ready to create your video?" body={`${includedClips.length} included clips are ready. Check the order and choose any preferred versions, then combine your clips into one video.`}
          actionLabel="Create final video" actionIcon="film" accessibilityLabel="Approve clips and stitch the film" onAction={approveStitch} busy={studioBusy || timelineUnavailable} colors={colors} /> : null}
        {!viewingFinal && !viewingImages && project.film && includedClipsReady && editDirty && !stitchInProgress && taskId ? <ReviewBanner title="Update your finished video" body="Your included scenes, clip order, or chosen versions have changed. Create a new export to include them; your previous export stays available."
          actionLabel="Create updated video" actionIcon="film" accessibilityLabel="Create updated video" onAction={approveStitch} busy={studioBusy || timelineUnavailable} colors={colors} /> : null}
        {!viewingFinal && !viewingImages && project.film && includedClipsReady && !editDirty && !stitchInProgress && taskId ? <View style={styles.dockRow}>
          <Text size="xs" color="secondary">Your video is exported. Create a new export to apply the current clips and order.</Text>
          <Pressable accessibilityRole="button" accessibilityLabel="Create new export" disabled={studioBusy || timelineUnavailable} onPress={approveStitch}
            style={[styles.actionChip, { borderColor: colors.border_color_secondary, opacity: studioBusy || timelineUnavailable ? 0.45 : 1 }]}>
            <Icon name="film" size={12} color={colors.text_color_secondary} /><Text size="xs">Create new export</Text>
          </Pressable>
        </View> : null}
        {stitchInProgress ? <View style={styles.dockStatus} accessibilityLiveRegion="polite">
          <Text size="s" weight="semibold">Your video has been queued</Text>
          <Text size="xs" color="secondary">Editing is locked while stitching. You can still preview and download clips. The new export will appear in Final video when it is ready.</Text>

        </View> : null}
        {stitchRequested && generationEnded ? <View style={styles.dockStatus}>
          <Text size="s" weight="semibold">Export needs attention</Text>
          <Pressable accessibilityRole="button" accessibilityLabel="Retry video export" disabled={studioBusy || timelineUnavailable || !includedClipsReady} onPress={approveStitch}
            style={[styles.actionChip, { borderColor: colors.border_color_secondary }]}><Text size="xs">No export arrived? Try again</Text></Pressable>
        </View> : null}
        {viewingImages && showImagesApproved ? <View style={styles.dockStatus} accessibilityLiveRegion="polite"><Text size="s" weight="semibold">Images locked while clips are generating</Text><Text size="xs" color="secondary">You can preview and download references. Image choices, revisions, and uploads will be available when generation finishes or stops.</Text></View> : null}
      </View> : null}
      <RedoSheet visible={!!redoTarget} item={redoTarget?.item} kind={redoTarget?.kind} previewUrl={resolveMediaUrl(redoTarget?.item, urls)} colors={colors} busy={studioBusy}
        onClose={() => setRedoTarget(null)} onSubmit={submitRedo} />
    </View>
  );
}

const styles = StyleSheet.create({
  workspaceBody: { flex: 1, minHeight: 0, minWidth: 0, flexDirection: 'row', gap: 14, paddingHorizontal: 14, paddingBottom: 14 },
  clipPreviewColumn: { flex: 0.75 },
  clipStageWrap: { flexShrink: 1, minHeight: 0 },
  clipStage: { height: 'auto', aspectRatio: 16 / 9, maxHeight: 260, minHeight: 0 },
  timelineStrip: { flexShrink: 0, borderTopWidth: 1, paddingBottom: 6 },
  timelineHead: { paddingHorizontal: 14, paddingTop: 8, paddingBottom: 4, gap: 4 },
  timelineContent: { flexDirection: 'row', paddingVertical: 2 },
  compactClipPoster: { height: 64 },
  // Reserve the short laptop workspace for the player and take controls.
  // The same timeline content remains available in a tighter vertical rhythm.
  denseClipPoster: { height: 48 },
  denseClipCard: { gap: 3 },
  denseCardHit: { gap: 3 },
  denseClipName: { paddingBottom: 0 },
  denseTimelineHead: { paddingTop: 4, paddingBottom: 2, gap: 2 },
  denseTimelineStrip: { paddingBottom: 2 },
  denseTimelineContent: { paddingVertical: 1 },
  denseReorderControl: { marginTop: 4 },
  denseWorkspaceNav: { paddingVertical: 6 },
  denseWorkspaceTab: { minHeight: 48, paddingVertical: 5 },
  denseWorkspaceIntro: { paddingTop: 6, paddingBottom: 8, gap: 2 },
  denseWorkflowDock: { paddingVertical: 8 },
  timelineCell: { width: 152, flexShrink: 0, padding: 4 },
  timelineInclusion: { minHeight: 28, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5 },
  timelineTakeSelection: { flexDirection: 'row', alignItems: 'center', minHeight: 16, gap: 4, marginTop: 2, paddingHorizontal: 2 },
  finalTakeSummary: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 2 },
  reorderControl: { alignSelf: 'center', flexDirection: 'row', alignItems: 'center', marginTop: 6, padding: 2, borderWidth: 1, borderRadius: 10 },
  reorderButton: { width: 32, height: 32, alignItems: 'center', justifyContent: 'center', borderRadius: 7 },
  reorderButtonPressed: { transform: [{ scale: 0.94 }] },
  reorderButtonDisabled: { opacity: 0.3 },
  reorderPosition: { minWidth: 32, paddingHorizontal: 4, height: 32, alignItems: 'center', justifyContent: 'center' },
  reorderNumber: { lineHeight: 16, textAlign: 'center', fontVariant: ['tabular-nums'], includeFontPadding: false },
  workspaceStack: { flexDirection: 'column', gap: 10 },
  previewColumn: { flex: 1.18, minWidth: 0, minHeight: 0, gap: 12, overflow: 'hidden' },
  previewColumnStack: { flex: 1.15 },
  toolsColumn: { flex: 1, minHeight: 0, minWidth: 0, borderWidth: 1, borderRadius: 12, overflow: 'hidden' },
  libraryBody: { flex: 1, minWidth: 0, minHeight: 0 },
  libraryHead: { padding: 12, gap: 10 },
  libraryTabs: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  libraryTab: { minHeight: 32, paddingHorizontal: 9, paddingVertical: 6, borderWidth: 1, borderRadius: 8, justifyContent: 'center' },
  libraryContent: { padding: 8 },
  toolsContent: { padding: 12, gap: 12 },
  assetGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  gridCell: { width: '50%', padding: 4 },
  fluidCard: { width: '100%' },
  fluidPoster: { height: 'auto', aspectRatio: 16 / 10 },
  inspectorScroll: { flex: 1, minHeight: 0, minWidth: 0 },
  inspectorContent: { padding: 2, paddingBottom: 10 },
  // Image controls keep their full height; the preview yields space first.
  imageInspector: { flexShrink: 0 },
  imageStageWrap: { flexShrink: 1, minHeight: 0 },
  imageStage: { flexShrink: 1, minHeight: 0 },
  inspectorActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  workflowDock: { flexShrink: 0, borderTopWidth: 1, padding: 12, gap: 6 },
  dockRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  dockStatus: { gap: 4 },
  workspaceNav: { flexDirection: 'row', gap: 4, padding: 10, borderBottomWidth: 1 },
  workspaceTab: { flex: 1, minWidth: 0, minHeight: 58, padding: 8, borderRadius: 12, borderWidth: 1, flexDirection: 'row', alignItems: 'center', gap: 8 },
  stepNumber: { width: 24, height: 24, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  workspaceIntro: { paddingHorizontal: 16, gap: 4, paddingTop: 10, paddingBottom: 12, flexShrink: 0 },
  sectionHeading: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 },
  finalPreviewContent: { padding: 2, paddingBottom: 12, gap: 16 },
  finalVersions: { gap: 10 },
  finalVersionsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  exportCard: { flexBasis: 200, flexGrow: 1, minWidth: 0, borderWidth: 1, borderRadius: 10, padding: 12, gap: 6 },
  changeCard: { padding: 0, borderWidth: 0, borderRadius: 0, gap: 12 },
  suggestionRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
  changeInput: { minHeight: 90, borderWidth: 1, borderRadius: 10, padding: 12, fontSize: 14, lineHeight: 20, textAlignVertical: 'top' },
  root: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    position: 'relative',
  },
  stageWrap: {
    backgroundColor: STAGE_BLACK,
    alignItems: 'center',
    flexShrink: 0,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingTop: 8,
  },
  stage: {
    width: '100%',
    maxWidth: 920,
    backgroundColor: STAGE_BLACK,
    borderRadius: 10,
    overflow: 'hidden',
    alignSelf: 'center',
  },
  stageImage: {
    width: '100%',
    height: '100%',
  },
  stageEmpty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 24,
  },
  stageEmptyIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  stageCaption: {
    width: '100%',
    minHeight: 32,
    paddingHorizontal: 2,
    paddingVertical: 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  section: {
    paddingHorizontal: 0,
    gap: 10,
  },
  sectionLabel: {
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  clipCard: {
    width: 144,
    borderWidth: 1,
    borderRadius: 10,
    padding: 4,
    gap: 6,
    position: 'relative',
  },
  cardHit: {
    gap: 6,
  },
  clipPoster: {
    height: 80,
    borderRadius: 7,
    overflow: 'hidden',
    position: 'relative',
  },
  clipPosterImage: {
    width: '100%',
    height: '100%',
  },
  clipPosterFallback: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clipBadge: {
    position: 'absolute',
    left: 5,
    bottom: 5,
    borderRadius: 4,
    paddingHorizontal: 5,
    paddingVertical: 1,
  },
  clipDot: {
    position: 'absolute',
    top: 5,
    right: 5,
    width: 7,
    height: 7,
    borderRadius: 4,
  },
  cardDownload: {
    position: 'absolute',
    top: 4,
    left: 4,
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: 'rgba(0,0,0,0.72)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  clipName: {
    paddingHorizontal: 2,
    paddingBottom: 2,
  },
  stillCard: {
    width: 120,
    borderWidth: 1,
    borderRadius: 10,
    padding: 4,
    gap: 6,
    position: 'relative',
  },
  stillPoster: {
    height: 88,
    borderRadius: 7,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  takeBadge: {
    position: 'absolute',
    right: 5,
    bottom: 5,
    borderRadius: 4,
    paddingHorizontal: 5,
    paddingVertical: 1,
  },
  stillTakeBadge: {
    right: 4,
    bottom: 4,
  },
  takeRow: {
    gap: 6,
    marginTop: 2,
    minWidth: 0,
    flexShrink: 0,
  },
  takeRowLabel: {
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  takeChips: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    flexShrink: 0,
    gap: 6,
  },
  takeRailContent: { paddingBottom: 4 },
  takeChip: {
    height: 40,
    width: 108,
    maxWidth: 108,
    flexShrink: 0,
    paddingHorizontal: 5,
    paddingVertical: 3,
    borderWidth: 1,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  takeThumb: {
    width: 40,
    height: 28,
    flexShrink: 0,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: STAGE_BLACK,
  },
  takeFinalBadge: { position: 'absolute', bottom: 1, right: 1, borderRadius: 8, backgroundColor: STAGE_BLACK, padding: 1 },
  sheetGhost: {
    minHeight: 36,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  inspector: {
    paddingHorizontal: 0,
    gap: 10,
  },
  inspectorHead: {
    gap: 2,
  },
  actionChip: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
    minHeight: 36,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderRadius: 8,
  },
  reviewCard: {
    padding: 8,
    borderWidth: 1,
    borderRadius: 10,
    gap: 12,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    ...Platform.select({
      web: {
        boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 10px 28px rgba(0,0,0,0.35)',
      },
      default: {},
    }),
  },
  reviewAction: {
    alignSelf: 'stretch',
    justifyContent: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minHeight: 44,
    paddingHorizontal: 14,
    borderRadius: 10,
    marginTop: 2,
  },
});
